// ─────────────────────────────────────────────────────────────────────────────
// WP-15 REQ-132 — Sección "Campañas" del Admin: lista + wizard de 5 pasos.
//
// Reutiliza la ESTRUCTURA del wizard legacy (SurveyBuilderView: Info → Audiencia → Config →
// Documentos → Publicar) sobre el CampaignsController. Pasos: Info → Alcance/Audiencia (OUs) →
// Configuración (fechas/visibilidad/puntos) → Documentos → Publicar/Aprobación.
// RN: los PUNTOS son inmutables si la campaña ya arrancó (estado Activa/Finalizada). Los documentos
// solo se gestionan tras crear el borrador. Aprobaciones pendientes = AdminGlobal o Admin
// Reconocimiento (RN-C11, ampliado 2026-08-03). Estados honestos.
// ─────────────────────────────────────────────────────────────────────────────
import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  useBrillamosCampaignsByEstadoVisible,
  useDeleteCampaign,
  useCampaigns,
  useCampaignSchedulesOverview,
  pickLocalized,
  useCreateCampaign,
  useUpdateCampaign,
  useDuplicateCampaign,
  usePanicCampaign,
  useUndoPanicCampaign,
  puedeDeshacerPanico,
  useGlobalPanic,
  useCampaignApproval,
  usePendingApprovals,
  useMyApprovals,
  useCampaignDocuments,
  useAddCampaignDocument,
  useDeleteCampaignDocument,
  useGestionSettings,
  useSaveGestionSettings,
  useProgramSettings,
  useSaveProgramSettings,
  useUserById,
  useCampaign,
  RecognitionApiError,
  getCampaignName,
  getRecognitionErrorMessage,
  getRecognitionErrorCode,
  STREAK_MINIMUM_FLOOR,
  STREAK_MINIMUM_CEILING,
  clampStreakMinimum,
  RECOGNITIONS_LIMIT_FLOOR,
  RECOGNITIONS_LIMIT_CEILING,
  clampRecognitionsLimit,
} from '@/api/recognition';
import type {
  CampaignDto, CampaignEstadoVisible, CampaignStatus, CampaignType, CampaignVisibilityDto, LocalizedText,
  CreateCampaignBody, UpdateCampaignBody, GestionSettingsDto,
  RecognitionProgramSettingsDto, CampaignExceptionDto,
} from '@/api/recognition';
import { RoleGuard, useEffectiveRoles, useHasRecognitionPermission } from '@/auth/RoleGuard';
import { hasOuScopedAdminRole, hasUnscopedRecognitionRole } from '@/auth/roleLabels';
import { programRulesDevEnabled } from '@/config/featureFlags';
import { ConfirmModal } from '@/components/common';
import { OuTreeSelect } from '@/components/common/OuTree';
import { useOUItems } from '@/hooks/useOUs';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useUnsavedChangesGuard } from '@/contexts/UnsavedChangesContext';
import type { OUItem } from '@/api/types';
import { StateBlock } from '../components';
import { AdminSection, AdminSaveBar, AdminToggle, LocalizedFields, ToggleHelp, hasAllLanguages, usePagination, AdminPagerFooter, BannerEditorFields, DocumentsManager } from './adminShared';
import { metaDeOu, normalizarNombreOu } from '../ouMeta';
import { CampaignExceptionsPanel } from './CampaignExceptions';
import { alcanceIncluyeGdpr, campanaFueraDeAlcance, collapseOuIds, expandOuIds, idsEnAlcance } from './ouScopeUtils';
import { todayInTimeZone, minFechaCampana, esFechaPasadaNoPermitida } from './campaignPastDates';
import { translationApi, SessionExpiredError } from '@/api/client';
import { SUPPORTED_LANGUAGE_CODES } from '@/i18n/languages';
import { asset } from '@/utils/basePath';

const TYPES: CampaignType[] = ['Fundamentales', 'Brillamos', 'Personalizada'];

/** IDs de OU → nombres legibles para mostrar (cae al id si no se encuentra, p. ej. dato legacy). */
const ouLabels = (ids: string[], items: OUItem[]): string =>
  ids.map((id) => items.find((o) => o.id === id)?.name ?? id).join(', ');

/**
 * WP-B9 (RF-071) — campañas ACTIVAS que chocan con este alcance, para avisar ANTES de llamar al
 * backend en vez de comerse el 409 después.
 *
 * 🔴 Replica la regla REAL del backend, no una mejor: `HasActiveOverlapAsync` es
 * `Estado == Activa && Alcance ∩ candidato ≠ ∅`, sin fechas. Dos campañas que no se solapan en el
 * calendario pero comparten una OU SÍ chocan para él, y una versión «más lista» que mirara fechas
 * dejaría pasar lo que el backend rechaza — que es el peor de los dos errores.
 *
 * 🔴 `alcanceExpandido`, nunca `draft.alcance`. Lo persistido es la lista EXPANDIDA (ver
 * `expandOuIds`); comparar la selección colapsada contra alcances expandidos da falsos negativos
 * en cuanto una de las dos campañas se seleccionó a nivel padre y la otra a nivel hijo.
 */
export const campanasEnConflicto = (
  alcanceExpandido: readonly string[],
  activas: readonly CampaignDto[],
  campanaId: string | undefined,
): CampaignDto[] => {
  if (alcanceExpandido.length === 0) return [];
  const propias = new Set(alcanceExpandido);
  return activas.filter((c) => c.id !== campanaId && (c.alcance ?? []).some((ou) => propias.has(ou)));
};

// `expandOuIds`/`idsEnAlcance`/`campanaFueraDeAlcance`/`collapseOuIds` viven ahora en `./ouScopeUtils`
// (SDD 2026-08-17 Tarea 5) — las usa también el wizard de excepciones de `CampaignExceptions.tsx`.
const STATUS_FILTERS: (CampaignStatus | 'all')[] = ['all', 'Borrador', 'Activa', 'Finalizada'];
// ADR 0014 — la pestaña Brillamos filtra con las MISMAS palabras que pinta su tabla (estado visible).
const BRILLAMOS_FILTERS: (CampaignEstadoVisible | 'all')[] =
  ['all', 'Borrador', 'PendienteAprobacion', 'PorIniciar', 'Activa', 'Finalizada'];

/**
 * C2/P17 — lo que se manda como `frecuencia` en el PATCH.
 *
 * El backend distingue tres cosas y la pantalla tiene que respetarlas:
 *   · `undefined` → no la toques
 *   · `''`        → bórrala
 *   · texto       → guárdala
 *
 * El wizard SIEMPRE manda lo que el usuario ve, incluida la cadena vacía: si mandara `undefined` al
 * vaciar el campo, borrar una frecuencia sería imposible desde la pantalla — se vería vacía y el
 * backend la conservaría. Se recorta el espacio en blanco para que «Mensual » y «Mensual» no acaben
 * siendo dos valores distintos en la tabla de Históricos.
 */
export function frecuenciaAGuardar(valor: string): string {
  return valor.trim();
}

/**
 * C2/P19 — lo que se manda como `metaParticipantes` en el PATCH.
 *
 * Bug UAT #19 — el asistente YA NO CAPTURA la meta (no aplica a Reconocimiento), así que esto ya no
 * traduce lo que teclea un admin: sanea el valor que se releyó del DTO para devolverlo intacto. Sin
 * campo que vaciar tampoco hay borrado que pedir, por eso el cuerpo dejó de llevar la bandera de
 * retirada: `null` significa «no la toques» y una meta guardada por API sobrevive a la edición.
 *
 * Un valor no numérico o ≤ 0 sale como `null` en vez de viajar: el backend lo rechazaría con un 422
 * (`RequireValidMetaParticipantes`) y bloquearía el guardado del resto de la campaña por un dato que
 * el admin ya no puede ni ver. Es la misma decisión que en los otros campos numéricos del asistente —
 * se sanea aquí y el servidor sigue siendo la autoridad.
 */
export function metaParticipantesAGuardar(valor: string): number | null {
  const n = Number(valor.trim());
  return valor.trim() !== '' && Number.isInteger(n) && n > 0 ? n : null;
}
const DEFAULT_TZ = 'America/Mexico_City';
/** Zonas horarias IANA para el selector (fallback al default si el navegador no expone supportedValuesOf). */
const TIMEZONES: string[] = (Intl as { supportedValuesOf?: (k: string) => string[] })
  .supportedValuesOf?.('timeZone') ?? [DEFAULT_TZ];

const DEFAULT_VIS: CampaignVisibilityDto = {
  mostrarRemitente: true, mostrarFundamental: true, mostrarTexto: true, mostrarHabito: true,
  enviados: true, recibidos: true, anonimos: false, entryMode: 'app', cargaMasiva: false, aplicanPuntos: false,
  reaccionesHabilitadas: true,
  // B8-3 — los defaults reproducen el comportamiento anterior: una campaña ya creada no cambia.
  mostrarEtiquetaCampana: true, permitirDesdeFundamentales: true,
  envioDesactivado: false,
  permitirAnonimos: null,
  // Decisión PO 2026-09-23 — la edición post-envío es de la campaña, sin herencia: una campaña nueva
  // nace con un valor explícito (apagado, el mismo default que tenía Gestión) en vez de `null`.
  allowPostEdit: false,
  // Fase 2 (Anti-Spoiler por campaña) — mismo criterio: nace apagado y explícito. Protege solo mientras
  // la campaña no esté cerrada.
  antiSpoiler: false,
  // Multi-persona / multi-fundamental por campaña (2026-09-29) — nacen sin valor y el asistente los
  // guarda explícitos con lo que heredaría (`multiEnvioEfectivo`) en cuanto lo conoce.
  multipersona: null, multiFundamental: null, fundamentalsLimitActive: null, maxFundamentalsPerRecognition: null,
  // Modalidad de entrada por campaña (2026-09-29) — nace sin valor y el asistente la guarda explícita
  // con la heredada (`modalidadEntradaEfectiva`) en cuanto la conoce.
  modalidadEntrada: null,
};

/** Reglas de envío de una campaña Brillamos tal como aplican (dentro de la campaña no hay piso de Europa
 *  ni excepción por OU — ADR 0005). */
export interface MultiEnvioEfectivo {
  multipersona: boolean;
  multiFundamental: boolean;
  fundamentalsLimitActive: boolean;
  maxFundamentalsPerRecognition: number;
}

/**
 * Multi-persona y multi-fundamental por campaña (pedido PO 2026-09-29) — el valor que la campaña usa:
 * el suyo, o si no lo tiene, el de la pestaña Brillamos, o el de Gestión, o el default del contrato.
 * Es la misma herencia que aplica el servidor (`MultiEnvioPolicy`) y que usa su relleno de despliegue.
 */
export function multiEnvioEfectivo(
  vis: CampaignVisibilityDto,
  programa?: RecognitionProgramSettingsDto | null,
  gestion?: GestionSettingsDto | null,
): MultiEnvioEfectivo {
  return {
    multipersona: vis.multipersona ?? programa?.multinomination ?? gestion?.multinomination ?? true,
    multiFundamental: vis.multiFundamental ?? programa?.multiFundamental ?? gestion?.multiFundamental ?? true,
    fundamentalsLimitActive: vis.fundamentalsLimitActive ?? programa?.fundamentalsLimitActive
      ?? gestion?.fundamentalsLimitActive ?? true,
    maxFundamentalsPerRecognition: vis.maxFundamentalsPerRecognition ?? programa?.maxFundamentalsPerRecognition
      ?? gestion?.maxFundamentalsPerRecognition ?? 2,
  };
}

type ModalidadEntrada = 'A' | 'B' | 'both';
const esModalidad = (v: unknown): v is ModalidadEntrada => v === 'A' || v === 'B' || v === 'both';

/**
 * Modalidad de entrada de una campaña Brillamos (pedido 2026-09-29): la suya o, si no la tiene, la de
 * la pestaña Brillamos, o la de Gestión, o 'A'. Misma herencia que el servidor (`ModalidadEntradaPolicy`)
 * y que su relleno de despliegue.
 */
export function modalidadEntradaEfectiva(
  vis: CampaignVisibilityDto,
  programa?: RecognitionProgramSettingsDto | null,
  gestion?: GestionSettingsDto | null,
): ModalidadEntrada {
  for (const v of [vis.modalidadEntrada, programa?.entryPointMode, gestion?.brillamosEntryPointMode]) {
    if (esModalidad(v)) return v;
  }
  return 'A';
}

interface CampaignDraft {
  id?: string;
  estado: CampaignStatus;
  nombre: LocalizedText;
  descripcion: LocalizedText;
  tipo: CampaignType;
  zonaHoraria: string;
  fechaInicio: string;
  fechaFin: string;
  alcance: string[];
  exclusiones: string[];
  visibilidad: CampaignVisibilityDto;
  landingSlug: string;
  /**
   * WP-02 (§26) — Config de diseño de la campaña (paso Diseño). Diccionario libre clave→valor
   * (menuIcon, menuTitle, secciones de la landing como 'true'/'false'). Persistido tal cual en
   * `CampaignDto.diseno`. La identidad de Brillamos es inmutable (REQ-034): el backend rechaza diseño
   * en campañas Brillamos, por eso solo se envía para las demás.
   */
  diseno: Record<string, string>;
  /**
   * RF-037 — Mínimo de racha propio de la campaña. Cadena vacía = sin mínimo propio (usa el global de
   * Gestión); se guarda como texto porque el input numérico puede quedar vacío y ese vacío es un
   * estado con significado, no un cero.
   */
  streakWeeklyMinimum: string;
  /**
   * Tope de envíos propio de la campaña. Cadena vacía = sin tope propio (usa el global de Gestión);
   * mismo patrón que `streakWeeklyMinimum` — a diferencia de ese campo, este SÍ es editable con la
   * campaña Activa (el tope se revisa por envío, no al cierre de un período).
   */
  maxRecognitionsPerPeriod: string;
  /**
   * Bug UAT #18 — «No aplica»: la campaña queda SIN tope de envíos, sin importar el global de Gestión.
   * Tercer estado, aparte de «número propio» (`maxRecognitionsPerPeriod` con valor) y «hereda global»
   * (vacío). Al prenderlo se ignora/limpia el número: son excluyentes.
   */
  recognitionsLimitDisabled: boolean;
  /**
   * Bug UAT #18 — Número propio de «veces que puedes reconocer el mismo fundamental a una persona».
   * Cadena vacía = hereda el global de Gestión. Mismo patrón que `maxRecognitionsPerPeriod`.
   */
  maxSameFundamentalPerPerson: string;
  /**
   * Bug UAT #18 — «No aplica» del mismo-fundamental: sin límite (ignora el global). Excluyente con el
   * número; en Europa el backend mantiene el piso GDPR aunque esto esté marcado.
   */
  sameFundamentalLimitDisabled: boolean;
  /**
   * C2/P17 — cada cuánto la campaña PIDE participación. Cadena vacía = sin declarar, y la tabla de
   * Históricos pinta «—». Es DECLARATIVA: se registra y se reporta, no dispara nada.
   */
  frecuencia: string;
  /**
   * C2/P19 — meta de PARTICIPANTES. Cadena vacía = sin meta.
   *
   * Bug UAT #19 — el paso «Configuración» YA NO LO CAPTURA (no aplica a Reconocimiento). Sigue en el
   * borrador a propósito: se lee del DTO y se devuelve intacto en el PATCH, de forma que una meta
   * fijada antes por API no se pierda al editar la campaña por cualquier otro motivo. Nada de la UI
   * lo escribe hoy.
   */
  metaParticipantes: string;
  /**
   * Banner "Brillamos Juntos" PROPIO de esta campaña (subtítulo + logo por idioma) — override del
   * banner global/de programa para el alcance de ESTA campaña, mismo patrón que
   * `CampaignExceptionOverride.bannerSubtitle/bannerLogoUrl` en el wizard "Campañas por OU"
   * (`CampaignExceptions.tsx`). Diccionario vacío = "no configurado, hereda el banner global/de
   * programa". No aplica a campañas Brillamos (identidad inmutable, REQ-034): se deshabilita en el
   * paso Diseño para ese tipo, igual que el resto de la sección.
   */
  bannerSubtitle: Record<string, string>;
  bannerLogoUrl: Record<string, string>;
}

function newDraft(tipo: CampaignType = 'Brillamos'): CampaignDraft {
  return {
    estado: 'Borrador', nombre: {}, descripcion: {}, tipo, zonaHoraria: DEFAULT_TZ,
    fechaInicio: '', fechaFin: '', alcance: [], exclusiones: [], visibilidad: { ...DEFAULT_VIS }, landingSlug: '',
    diseno: {},
    bannerSubtitle: {}, bannerLogoUrl: {},
    streakWeeklyMinimum: '',
    maxRecognitionsPerPeriod: '',
    recognitionsLimitDisabled: false,
    maxSameFundamentalPerPerson: '',
    sameFundamentalLimitDisabled: false,
    frecuencia: '',
    metaParticipantes: '',
  };
}

/**
 * Texto del input → mínimo de la campaña: un número (decimal redondeado y acotado a 1–20) o `'clear'`
 * cuando el campo está vacío, que devuelve la campaña al mínimo global.
 *
 * LIMITACIÓN CONOCIDA: un `<input type="number">` entrega `''` para cualquier contenido que el
 * navegador no consigue interpretar como número, así que una errata ("3e", "1.2.3") es
 * indistinguible del campo vacío en el DOM y se guarda como borrado del mínimo. El campo queda
 * visiblemente vacío al perder el foco, así que no es silencioso, pero tampoco avisa. Distinguirlo
 * exigiría un input de texto y perder los controles de incremento, que en un rango 1–20 sí se usan.
 */
const parseStreakMinimum = (raw: string): number | 'clear' => {
  if (raw.trim() === '') return 'clear';
  const n = Number(raw);
  return Number.isFinite(n) ? clampStreakMinimum(n) : 'clear';
};

/** Texto del input → tope de envíos de la campaña: un número (acotado a 1–1000) o `'clear'` cuando el campo está vacío. */
const parseMaxRecognitionsPerPeriod = (raw: string): number | 'clear' => {
  if (raw.trim() === '') return 'clear';
  const n = Number(raw);
  return Number.isFinite(n) ? clampRecognitionsLimit(n) : 'clear';
};

function toDraft(c: CampaignDto): CampaignDraft {
  return {
    id: c.id, estado: c.estado, nombre: { ...c.nombre }, descripcion: { ...(c.descripcion ?? {}) },
    tipo: c.tipo, zonaHoraria: c.zonaHoraria || DEFAULT_TZ,
    fechaInicio: c.fechaInicio ? c.fechaInicio.slice(0, 10) : '',
    fechaFin: c.fechaFin ? c.fechaFin.slice(0, 10) : '',
    alcance: [...c.alcance], exclusiones: [...c.exclusiones],
    visibilidad: { ...DEFAULT_VIS, ...c.visibilidad }, landingSlug: c.landingSlug ?? '',
    diseno: { ...((c.diseno as Record<string, string>) ?? {}) },
    bannerSubtitle: { ...(c.bannerSubtitle ?? {}) },
    bannerLogoUrl: { ...(c.bannerLogoUrl ?? {}) },
    streakWeeklyMinimum: c.streakWeeklyMinimum == null ? '' : String(c.streakWeeklyMinimum),
    maxRecognitionsPerPeriod: c.maxRecognitionsPerPeriod == null ? '' : String(c.maxRecognitionsPerPeriod),
    recognitionsLimitDisabled: c.recognitionsLimitDisabled ?? false,
    maxSameFundamentalPerPerson: c.maxSameFundamentalPerPerson == null ? '' : String(c.maxSameFundamentalPerPerson),
    sameFundamentalLimitDisabled: c.sameFundamentalLimitDisabled ?? false,
    frecuencia: c.frecuencia ?? '',
    metaParticipantes: c.metaParticipantes == null ? '' : String(c.metaParticipantes),
  };
}

/**
 * H-10 — el backend compara estas fechas por DÍA UTC del instante (`IsWithinDateWindow`,
 * `DateOnly.FromDateTime(...)` en el resto de campos de fecha del dominio), no por hora local. La
 * versión anterior (`toIso`, retirada) construía la medianoche LOCAL del navegador del admin: un
 * admin en UTC+ (de/nl/fr/ro/pt son de primera clase aquí) guardaba "15 de enero" como
 * `…-14T23:00:00Z` ⇒ día UTC 14 ⇒ el evento ocurría un día antes de lo elegido. Fijar la medianoche en
 * UTC directamente hace que el día elegido sea el día que se compara, sin importar el huso de quien
 * lo captura.
 */
const toUtcDayIso = (d: string): string | null => (d ? `${d}T00:00:00.000Z` : null);

/**
 * Bug Sesión #97 — mismo razonamiento que `toUtcDayIso`, pero para el FIN de una ventana en vez del
 * inicio. `fechaFin` usaba `toIso`/medianoche de INICIO del día elegido: `IsWithinDateWindow` exige
 * `now <= FechaFin`, así que una campaña con fin "18 de agosto" dejaba de estar activa justo cuando
 * ARRANCABA el 18, no cuando terminaba — perdía casi el día completo que el admin veía marcado como
 * último día vigente. Reproducido en vivo: con una ventana de 1-2 días la pérdida es casi la mitad de
 * la vigencia real, y explica reportes de "el tope de la campaña ya no aplica" durante lo que el admin
 * consideraba el último día. `FechaInicio` sigue usando `toUtcDayIso` (inicio de día): solo el FIN
 * necesita este ajuste, por la asimetría de `<=` contra `>=` en `IsWithinDateWindow`.
 */
const toUtcDayEndIso = (d: string): string | null => (d ? `${d}T23:59:59.999Z` : null);

/**
 * Bug UAT #113 — Códigos de rechazo del alta/edición de campaña → la MISMA frase que el wizard ya
 * muestra en su validación inline. Que el servidor y el formulario digan lo mismo es deliberado: el
 * admin no tiene por qué aprender dos redacciones de un solo problema.
 *
 * Antes, cualquier rechazo del backend se veía como el `title` genérico del 400 ("Error de validación
 * en los datos enviados") —literalmente lo que reportó Europa—, en español y sin decir qué campo.
 * Los códigos los declara `CreateCampaignCommandValidator` con `WithErrorCode`.
 */
const CAMPAIGN_ERROR_CODE_KEYS: Readonly<Record<string, string>> = {
  'campaign.nameRequired': 'admin.campaigns.valNameAllLangs',
  'campaign.scopeRequired': 'admin.campaigns.valScope',
  'campaign.timezoneRequired': 'admin.campaigns.valTimezone',
  'campaign.endBeforeStart': 'admin.campaigns.valDateOrder',
};

/**
 * Mensaje para un error de guardado de campaña: la traducción del código si el backend lo mandó y lo
 * conocemos, y si no el texto del backend (que sigue viniendo en español) como último recurso. Un
 * código NUEVO que este front todavía no mapee cae al fallback en vez de pintar una clave cruda.
 */
function campaignErrorMessage(
  error: unknown,
  t: (key: string, options?: Record<string, string>) => string,
): string {
  const coded = getRecognitionErrorCode(error);
  const key = coded ? CAMPAIGN_ERROR_CODE_KEYS[coded.code] : undefined;
  if (key) return t(key, coded?.params);
  return getRecognitionErrorMessage(error, t('admin.saveError'));
}

/**
 * 🔴 Bug UAT #114 (retest 19-ago) — «SI PERMITE CREAR CAMPAÑA POR OU, PERO NO SE REFLEJA EN LOS
 * USUARIOS». `Estado` y la ventana de fechas están DESACOPLADOS, y la lista solo miraba `Estado`:
 * una campaña `Activa` cuya vigencia ya cerró (o que todavía no arranca) se pintaba idéntica a una
 * vigente, así que el administrador veía "Activa" y no tenía forma de entender por qué el
 * colaborador seguía viendo «Reconocer».
 *
 * Medido en QA: «Prueba #1000» llevaba ~16 h vencida y seguía `Activa` (el barrido
 * `ExpireDueCampaigns` no corre en ese entorno), y de 15 campañas creadas desde el 16-ago, 13
 * acabaron Finalizadas — varias activadas ya vencidas.
 *
 * El criterio es el MISMO que el del backend para expirar (`ExpireDueCampaigns`: `FechaFin <=
 * ahora`) y el que ya expone el dominio en `Campaign.IsWithinDateWindow`. No se inventa una regla
 * nueva: se deja de esconder la que ya existe.
 */
export type VigenciaCampana = 'vigente' | 'porIniciar' | 'vencida';

export function vigenciaDeCampana(
  campana: Pick<CampaignDto, 'fechaInicio' | 'fechaFin'>,
  ahora: Date = new Date(),
): VigenciaCampana {
  const instante = (valor: string | null | undefined): number | null => {
    if (!valor) return null;
    const ms = new Date(valor).getTime();
    return Number.isNaN(ms) ? null : ms;
  };
  const fin = instante(campana.fechaFin);
  const inicio = instante(campana.fechaInicio);
  // Fecha ilegible cuenta como "sin límite por ese lado", igual que `null`: una campaña vigente no
  // puede pasar a "vencida" por un dato que no se pudo leer.
  if (fin !== null && ahora.getTime() > fin) return 'vencida';
  if (inicio !== null && ahora.getTime() < inicio) return 'porIniciar';
  return 'vigente';
}

function statusBadge(
  estado: CampaignStatus, pendienteAprobacion = false, vigencia: VigenciaCampana = 'vigente',
): string {
  if (pendienteAprobacion) return 'adm-badge adm-badge--info';
  // Activa pero fuera de su ventana: en ámbar, no en verde. El verde afirma "esto está corriendo",
  // y es justo lo que no está pasando.
  if (estado === 'Activa' && vigencia !== 'vigente') return 'adm-badge adm-badge--warn';
  if (estado === 'Activa') return 'adm-badge adm-badge--on';
  if (estado === 'Borrador') return 'adm-badge adm-badge--warn';
  return 'adm-badge';
}

// Bug #74 — un Borrador con una solicitud de aprobación abierta (`CampaignApprovalDto.estado ===
// "pendiente"`) seguía mostrando el badge "Borrador" en el listado: `Campaign.Estado` no cambia al
// enviar a aprobación (solo lo hace al aprobarse, vía ActivateCampaignCommand), y el estatus de
// aprobación vive en una colección aparte, nunca unido al DTO de la campaña. Solo afecta al LABEL;
// `Campaign.Estado` sigue siendo la fuente de verdad para todo lo demás (edición, activación, etc.).
function statusLabel(
  t: (key: string) => string, estado: CampaignStatus, pendienteAprobacion: boolean,
  vigencia: VigenciaCampana = 'vigente',
): string {
  if (pendienteAprobacion) return t('admin.campaigns.status.PendienteAprobacion');
  // La vigencia solo matiza a las ACTIVAS: en un Borrador o una Finalizada, el estado ya explica por
  // sí solo que no está gobernando nada, y añadirle "fuera de ventana" sería ruido.
  if (estado === 'Activa' && vigencia === 'vencida') return t('admin.campaigns.status.ActivaVencida');
  if (estado === 'Activa' && vigencia === 'porIniciar') return t('admin.campaigns.status.ActivaPorIniciar');
  return t(`admin.campaigns.status.${estado}`);
}

/**
 * ADR 0014 — estado visible de una campaña Brillamos (Borrador, Pendiente de aprobación, Por iniciar,
 * Activa, Finalizada). Lo calcula el servidor; la web no recalcula la vigencia. Si el campo no llega
 * (servidor anterior al contrato), se cae al estado GUARDADO sin matiz, nunca a un cálculo propio.
 */
export function estadoVisibleBrillamos(c: Pick<CampaignDto, 'estado' | 'estadoVisible'>): CampaignEstadoVisible {
  return c.estadoVisible ?? c.estado;
}

function badgeEstadoVisible(visible: CampaignEstadoVisible): string {
  switch (visible) {
    case 'Activa': return 'adm-badge adm-badge--on';
    case 'PendienteAprobacion': return 'adm-badge adm-badge--info';
    case 'Borrador':
    case 'PorIniciar': return 'adm-badge adm-badge--warn';
    default: return 'adm-badge';
  }
}

// `focusCampaignId` (deep-link `?aprobacion={id}`): la cola de aprobación abre ya expandida ESA campaña.
type Mode = { kind: 'list' } | { kind: 'wizard'; draft: CampaignDraft } | { kind: 'approvals'; focusCampaignId?: string };

// WP-00 (§26) — 3 tabs por tipo, paridad con la maqueta (fund-puntos-tabs, subrayado naranja).
type CampType = 'fundamentales' | 'brillamos' | 'personalizadas';

/** Las tres acciones de "botón de pánico" comparten un único `ConfirmModal` estilizado en vez de
 * `window.confirm()` (diálogo nativo del navegador, sin estilo — ver auditoría de confirmaciones). */
type PanicConfirm =
  | { kind: 'global' }
  | { kind: 'panic'; campaign: CampaignDto }
  | { kind: 'undo'; campaign: CampaignDto };

/**
 * Ticket 04 (estados de campaña Brillamos) — ¿se ofrece «Borrar» en esta fila? Solo en Brillamos, solo
 * con estado visible Borrador o Pendiente de aprobación (lo que nunca corrió), y solo dentro del
 * alcance del admin (misma regla que Editar). Fuera de alcance se OCULTA, no se deshabilita: borrar
 * algo ajeno no es una acción que el admin deba ver a medias.
 */
export function puedeBorrarCampana(c: Pick<CampaignDto, 'tipo' | 'estado' | 'estadoVisible'>, fueraDeAlcance: boolean): boolean {
  if (c.tipo !== 'Brillamos' || fueraDeAlcance) return false;
  const visible = estadoVisibleBrillamos(c);
  return visible === 'Borrador' || visible === 'PendienteAprobacion';
}

/**
 * ¿Se ofrece «Editar»? Nunca en una Finalizada. En Brillamos manda el estado VISIBLE: una campaña con la
 * fecha de fin vencida que `ExpireDueCampaigns` todavía no cerró sigue guardada como `Activa`, pero ya es
 * Finalizada (CONTEXT.md, ADR 0014) y, como toda Finalizada, es terminal: para revivirla está Duplicar.
 * Mirar solo el estado guardado dejaba la etiqueta «Finalizada» con «Editar» al lado. Personalizadas
 * conserva su regla de siempre (spec: sin cambios en sus acciones).
 */
export function puedeEditarCampana(c: Pick<CampaignDto, 'tipo' | 'estado' | 'estadoVisible'>): boolean {
  return c.tipo === 'Brillamos' ? estadoVisibleBrillamos(c) !== 'Finalizada' : c.estado !== 'Finalizada';
}

/**
 * ¿Se ofrece «Detener Campaña» (pánico)? Sobre lo que todavía gobierna o va a gobernar: en Brillamos,
 * Activa o Por iniciar (detener una programada es cancelarla). Una vencida ya no gobierna a nadie, y el
 * servidor la marcaría «cerrada por pánico» sin que pudiera deshacerse. Personalizadas: estado guardado.
 */
export function puedeDetenerCampana(c: Pick<CampaignDto, 'tipo' | 'estado' | 'estadoVisible'>): boolean {
  if (c.tipo !== 'Brillamos') return c.estado === 'Activa';
  const visible = estadoVisibleBrillamos(c);
  return c.estado === 'Activa' && (visible === 'Activa' || visible === 'PorIniciar');
}

/** Banderas por idioma para los chips del banner (1:1 maqueta: 🇲🇽 es, etc.). */
// 🔴 `pt` lleva 🇵🇹, no 🇧🇷: la maqueta usa la bandera de PORTUGAL en sus tres listas de idiomas
// (`_I18N_FLAGS` y las dos tablas de `fundamentales-admin.js`), y el portugués del producto es el de
// Portugal. La bandera de Brasil le decía al admin la variante equivocada.
const BANNER_LANG_FLAG: Record<string, string> = { es: '🇲🇽', en: '🇺🇸', de: '🇩🇪', fr: '🇫🇷', nl: '🇳🇱', pt: '🇵🇹', ro: '🇷🇴' };
const CAMP_TABS: ReadonlyArray<{ id: CampType; emoji: string; labelKey: string }> = [
  { id: 'fundamentales', emoji: '⭐', labelKey: 'admin.campaigns.tabFundamentales' },
  { id: 'brillamos', emoji: '✨', labelKey: 'admin.campaigns.tabBrillamos' },
  { id: 'personalizadas', emoji: '🎯', labelKey: 'admin.campaigns.tabPersonalizadas' },
];

/**
 * Tarea 7 (SDD 2026-08-17, Grupo C) — el programa que elige un Admin OU para su ÚNICA superficie
 * (el panel de excepciones "Campañas por OU"): sin "Personalizadas", porque `CampaignExceptionsPanel`
 * solo tiene contexto Fundamentales/Brillamos (la excepción es a nivel PROGRAMA, no de campaña
 * concreta — ver `ExcContext` en `CampaignExceptions.tsx`).
 */
type OuScopedProgram = Exclude<CampType, 'personalizadas'>;

/** Mismos emoji/etiquetas que `CAMP_TABS` (reutiliza las claves i18n ya existentes), sin "Personalizadas". */
const OU_SCOPE_PROGRAM_TABS: ReadonlyArray<{ id: OuScopedProgram; emoji: string; labelKey: string }> = [
  { id: 'fundamentales', emoji: '⭐', labelKey: 'admin.campaigns.tabFundamentales' },
  { id: 'brillamos', emoji: '✨', labelKey: 'admin.campaigns.tabBrillamos' },
];

/**
 * Tab "Fundamentales" (WP-07 v1): el default SIEMPRE ACTIVO (RN-C10, sin fila de campaña ni fechas).
 * Muestra el estado + las REGLAS DE ENVÍO cableadas al backend REAL (GestionSettingsDto, el mismo
 * documento que edita la sección Gestión — fuente única, sin datos falsos). El diseño del banner por
 * idioma y las modalidades Entry A/B necesitan extender el modelo (backend) → siguiente rebanada WP-07.2.
 */
function FundamentalesPanel() {
  const { t } = useTranslation('recognition');
  const query = useGestionSettings();
  const save = useSaveGestionSettings();
  const [draft, setDraft] = useState<GestionSettingsDto | null>(null);
  const [dirty, setDirty] = useState(false);
  // 🔴 SEPARACIÓN POR PROGRAMA: las reglas de envío/topes/modalidad/moderación (incl. anti-spoiler)
  // PROPIAS de Fundamentales se guardan en su DOC DE PROGRAMA (recognition_program_settings/Fundamentales),
  // NO en el global — así editarlas no arrastra a Brillamos (que hereda el global sin override propio).
  // Banner, racha, badges y anónimos SÍ siguen en el global de Gestión (no tienen override por programa).
  // Efectivo por-regla = pDraft.X ?? global.X ?? default, idéntico al panel de Brillamos
  // (BrillamosRulesPanel). El global sigue siendo el baseline de ambos. (Corregido 2026-08-22, fila
  // 65/61: allowPostEdit y antiSpoiler estaban escribiendo al global por un wiring que nunca se
  // actualizó cuando el backend pasó a resolverlos por-programa.)
  const pQuery = useProgramSettings('Fundamentales');
  const pSave = useSaveProgramSettings('Fundamentales');
  const [pDraft, setPDraft] = useState<RecognitionProgramSettingsDto | null>(null);
  const [pDirty, setPDirty] = useState(false);
  // Vista (maqueta): 👑 Admin Global edita; 🏢 Admin OU/Embajador ve la config general en SOLO LECTURA
  // (la config global la fija el Admin Global; el OU crea excepciones por localidad que pasan por aprobación).
  const [vista, setVista] = useState<'global' | 'ou'>('global');

  // Sincroniza el borrador al llegar el documento Y ante refetches en 2º plano (staleTime 5min +
  // refetch-on-focus), pero SOLO si el usuario no tiene cambios sin guardar — si no, un refetch pisaría
  // su edición, y como el PUT reemplaza el doc COMPLETO, guardar revertiría en silencio cambios que
  // este panel ni muestra (badges/racha) hechos desde la sección Gestión. Patrón de AdminPoints.
  useEffect(() => {
    setDraft((cur) => (query.data && (cur === null || !dirty) ? query.data : cur));
  }, [query.data, dirty]);
  useEffect(() => {
    setPDraft((cur) => (pQuery.data && (cur === null || !pDirty) ? pQuery.data : cur));
  }, [pQuery.data, pDirty]);
  const patch = (p: Partial<GestionSettingsDto>) => { setDraft((d) => (d ? { ...d, ...p } : d)); setDirty(true); };
  // pPatch escribe un override propio de Fundamentales; un campo nunca tocado queda null = hereda global.
  const pPatch = (p: Partial<RecognitionProgramSettingsDto>) => { setPDraft((d) => (d ? { ...d, ...p } : d)); setPDirty(true); };
  const doSave = () => {
    if (draft) save.mutate(draft, { onSuccess: () => setDirty(false) });
    if (pDraft) pSave.mutate(pDraft, { onSuccess: () => setPDirty(false) });
  };
  const anyDirty = dirty || pDirty;
  const savingAny = save.isPending || pSave.isPending;
  const savedAny = save.isSuccess && pSave.isSuccess;
  const periodLabel = (p: string) => t(p === 'monthly' ? 'admin.campaigns.periodMonthly' : 'admin.campaigns.periodWeekly');

  // WP-07.2 — diseño del banner por idioma (réplica de la maqueta). El chip elige el idioma a editar.
  const [bannerLang, setBannerLang] = useState('es');
  const bannerText = (field: 'bannerTitleLine1' | 'bannerTitleLine2' | 'bannerSubtitle'): string =>
    ((draft?.[field] as LocalizedText | null | undefined) ?? {})[bannerLang] ?? '';
  const patchBanner = (field: 'bannerTitleLine1' | 'bannerTitleLine2' | 'bannerSubtitle', value: string) => {
    const cur = (draft?.[field] as LocalizedText | null | undefined) ?? {};
    patch({ [field]: { ...cur, [bannerLang]: value } } as Partial<GestionSettingsDto>);
  };
  // Traduce los 3 campos del banner (título L1/L2 + subtítulo) desde `bannerLang` (idioma con el chip
  // activo) hacia los otros 6, reusando el mismo Azure Translator que `handleTranslateInfo` del wizard.
  const [bannerTranslating, setBannerTranslating] = useState(false);
  const [bannerTranslateError, setBannerTranslateError] = useState<string | null>(null);
  const handleTranslateBanner = async () => {
    if (bannerTranslating) return;
    setBannerTranslateError(null);
    const targetLangs = SUPPORTED_LANGUAGE_CODES.filter((code) => code !== bannerLang);
    const texts = [bannerText('bannerTitleLine1'), bannerText('bannerTitleLine2'), bannerText('bannerSubtitle')];
    setBannerTranslating(true);
    try {
      const response = await translationApi.translateTexts({ sourceLang: bannerLang, targetLangs, texts });
      let next = draft;
      (['bannerTitleLine1', 'bannerTitleLine2', 'bannerSubtitle'] as const).forEach((field, idx) => {
        const cur = (next?.[field] as LocalizedText | null | undefined) ?? {};
        const filled = { ...cur };
        targetLangs.forEach((code) => { filled[code] = response.translations[code]?.[idx] ?? filled[code] ?? ''; });
        next = next ? { ...next, [field]: filled } : next;
      });
      if (next) { setDraft(next); setDirty(true); }
    } catch (error) {
      setBannerTranslateError(
        error instanceof SessionExpiredError
          ? t('admin.translate.sessionExpired')
          : getRecognitionErrorMessage(error, t('admin.translate.error')),
      );
    } finally {
      setBannerTranslating(false);
    }
  };
  // WP-07.2 logo — YA se ve por defecto con el asset de FUNDAMENTALS del web (SVG por idioma); "Subir PNG"
  // lo sobre-escribe guardándolo como data-URI en `bannerLogoUrl[lang]` (sin blob nuevo). Volver al default
  // = quitar la clave. Idiomas con asset propio: es/en/fr/pt/ro (los demás caen al genérico).
  const logoFileRef = useRef<HTMLInputElement>(null);
  const [logoError, setLogoError] = useState<string | null>(null);
  const DEFAULT_LOGO_LANGS = ['es', 'en', 'fr', 'pt', 'ro'];
  const defaultLogoFor = (lang: string) => {
    const suffix = DEFAULT_LOGO_LANGS.includes(lang) ? `-${lang}` : '';
    // `asset()` va AQUI y no sobre `currentLogo`: el logo subido por el admin es un data-URI y
    // prefijarlo lo romperia. Solo la ruta por defecto necesita el prefijo de ambiente.
    return asset(`/assets/logo-fundamentales${suffix}.svg`);
  };
  const uploadedLogo = draft?.bannerLogoUrl?.[bannerLang];
  const currentLogo = uploadedLogo || defaultLogoFor(bannerLang);
  const setBannerLogo = (value: string) => {
    const cur = draft?.bannerLogoUrl ?? {};
    const next = { ...cur };
    if (value) next[bannerLang] = value; else delete next[bannerLang];
    patch({ bannerLogoUrl: next });
  };
  const MAX_LOGO_BYTES = 500 * 1024; // 500 KB: el logo va embebido en el doc de Gestión; se acota el tamaño.
  const handleLogoFile = (file: File | null) => {
    setLogoError(null);
    if (!file) return;
    if (file.type !== 'image/png') { setLogoError(t('admin.campaigns.bannerLogoErrPng')); return; }
    if (file.size > MAX_LOGO_BYTES) { setLogoError(t('admin.campaigns.bannerLogoErrSize')); return; }
    const reader = new FileReader();
    reader.onload = () => setBannerLogo(String(reader.result));
    reader.onerror = () => setLogoError(t('admin.campaigns.bannerLogoErrPng'));
    reader.readAsDataURL(file);
  };

  return (
    <div className="adm-camp-fund">
      {/* Vista (maqueta): perspectiva Admin Global (edita) / Admin OU-Embajador (solo lectura). Va ARRIBA
          del card "Siempre Activo" (orden 1:1 maqueta). */}
      <div className="adm-camp-vista">
        <span className="adm-camp-vista-label">{t('admin.campaigns.vista')}</span>
        <button type="button" aria-pressed={vista === 'global'} className={`adm-camp-vista-btn ${vista === 'global' ? 'adm-camp-vista-btn--on' : ''}`} onClick={() => setVista('global')}>👑 {t('admin.campaigns.vistaGlobal')}</button>
        <button type="button" aria-pressed={vista === 'ou'} className={`adm-camp-vista-btn ${vista === 'ou' ? 'adm-camp-vista-btn--on' : ''}`} onClick={() => setVista('ou')}>🏢 {t('admin.campaigns.vistaOu')}</button>
      </div>

      <div className="adm-camp-active-card">
        <i className="fas fa-circle-check" aria-hidden="true" />
        <div>
          <p className="adm-camp-active-title">{t('admin.campaigns.fundActiveTitle')}</p>
          <p className="adm-camp-active-desc">{t('admin.campaigns.fundActiveDesc')}</p>
        </div>
      </div>

      <StateBlock
        isLoading={query.isLoading || pQuery.isLoading}
        isError={query.isError || pQuery.isError}
        error={query.error ?? pQuery.error}
        isEmpty={!draft || !pDraft}
        emptyText={t('common.empty')}
        onRetry={() => { query.refetch(); pQuery.refetch(); }}
      >
        {draft && pDraft && vista === 'global' && (<>
          {/* WP-07.2 — DISEÑO DEL BANNER por idioma (réplica maqueta): chips + campos + vista previa en vivo */}
          <h4 className="adm-camp-fund-h">{t('admin.campaigns.bannerDesign')}</h4>
          <p className="adm-note">{t('admin.campaigns.bannerDesignHint')}</p>
          <div className="adm-banner-langs">
            {SUPPORTED_LANGUAGE_CODES.map((code) => (
              <button key={code} type="button"
                className={`adm-banner-lang ${bannerLang === code ? 'adm-banner-lang--on' : ''}`}
                onClick={() => setBannerLang(code)}><span aria-hidden="true">{BANNER_LANG_FLAG[code] ?? ''}</span> {code.toUpperCase()}</button>
            ))}
          </div>
          <div className="adm-banner-grid">
            <div className="adm-banner-fields">
              <label className="adm-field"><span>{t('admin.campaigns.bannerTitle1')}</span>
                <input type="text" value={bannerText('bannerTitleLine1')} onChange={(e) => patchBanner('bannerTitleLine1', e.target.value)} /></label>
              <label className="adm-field"><span>{t('admin.campaigns.bannerTitle2')}</span>
                <input type="text" value={bannerText('bannerTitleLine2')} onChange={(e) => patchBanner('bannerTitleLine2', e.target.value)} /></label>
              <label className="adm-field"><span>{t('admin.campaigns.bannerSubtitle')}</span>
                <textarea rows={2} value={bannerText('bannerSubtitle')} onChange={(e) => patchBanner('bannerSubtitle', e.target.value)} /></label>
              <div className="admin-docs-actions">
                <button
                  type="button"
                  className="btn btn--secondary"
                  disabled={bannerTranslating || (!bannerText('bannerTitleLine1') && !bannerText('bannerTitleLine2') && !bannerText('bannerSubtitle'))}
                  onClick={handleTranslateBanner}
                >
                  <i className={`fas ${bannerTranslating ? 'fa-spinner fa-spin' : 'fa-language'}`} />
                  {' '}
                  {bannerTranslating ? t('admin.translate.loading') : t('admin.translate.button')}
                </button>
              </div>
              {bannerTranslateError && <p className="adm-error" role="alert"><i className="fas fa-triangle-exclamation" /> {bannerTranslateError}</p>}
              <div className="adm-field">
                <span>{t('admin.campaigns.bannerLogo')}</span>
                <div className="adm-banner-logo-row">
                  <img src={currentLogo} alt="" className="adm-banner-logo-thumb" />
                  <input ref={logoFileRef} type="file" accept="image/png" className="adm-hidden-input" onChange={(e) => { handleLogoFile(e.target.files?.[0] ?? null); e.target.value = ''; }} />
                  <button type="button" className="btn btn--secondary" onClick={() => logoFileRef.current?.click()}>
                    <i className="fas fa-upload" /> {t('admin.campaigns.bannerLogoUpload')}
                  </button>
                  {uploadedLogo && <button type="button" className="btn btn--link" onClick={() => setBannerLogo('')}>{t('admin.campaigns.bannerLogoReset')}</button>}
                </div>
                {logoError && <p className="adm-error" role="alert"><i className="fas fa-triangle-exclamation" /> {logoError}</p>}
                <small className="adm-note">{t('admin.campaigns.bannerLogoHint')}</small>
              </div>
            </div>
            <div className="adm-banner-preview">
              <span className="adm-banner-preview-label">{t('admin.campaigns.bannerPreview')}</span>
              <div className="adm-banner-card">
                {/* T-02 — preview 1:1 con el hero real: L1 y L2 en renglones separados (bloques), no
                    concatenadas en una sola línea. Vacío total ⇒ placeholder. */}
                <p className="adm-banner-card-title">
                  {(bannerText('bannerTitleLine1') || bannerText('bannerTitleLine2'))
                    ? (<>
                        {bannerText('bannerTitleLine1') && <span className="adm-banner-card-line">{bannerText('bannerTitleLine1')}</span>}
                        {bannerText('bannerTitleLine2') && <span className="adm-banner-card-line">{bannerText('bannerTitleLine2')}</span>}
                      </>)
                    : t('admin.campaigns.bannerPreviewEmpty')}
                </p>
                <img src={currentLogo} alt="" className="adm-banner-card-logo" />
                {bannerText('bannerSubtitle') && <p className="adm-banner-card-sub">{bannerText('bannerSubtitle')}</p>}
                <span className="adm-banner-card-btn"><i className="fas fa-star" /> {t('admin.campaigns.bannerCta')}</span>
              </div>
            </div>
          </div>

          {/* WP-07.2 — MODALIDAD DE RECONOCIMIENTO (entry points). 1:1 maqueta: SWITCHES (no botones). El
              modelo es single-select (entryPointMode A|B|both), así que los 3 switches se comportan como radio
              — exactamente uno encendido = el modo actual; no hay estado inválido. */}
          <h4 className="adm-camp-fund-h">{t('admin.campaigns.entryPoints')} <ToggleHelp tooltipKey="entryPoints" /></h4>
          <div className="adm-toggles-row adm-toggles-row--cards">
            {(['A', 'B', 'both'] as const).map((m) => (
              <label key={m} className="adm-inline-toggle adm-inline-toggle--switch">
                <input type="checkbox" checked={(pDraft.entryPointMode ?? draft.entryPointMode ?? 'A') === m} onChange={() => pPatch({ entryPointMode: m })} /> {t(`admin.campaigns.entry.${m}`)}
              </label>
            ))}
          </div>

          {/* REGLAS DE ENVÍO (maqueta) */}
          <h4 className="adm-camp-fund-h">{t('admin.campaigns.rulesTitle')}</h4>
          <div className="adm-toggles-row adm-toggles-row--cards">
            <label className="adm-toggle-row"><AdminToggle isOn={pDraft.multinomination ?? draft.multinomination} onChange={(v) => pPatch({ multinomination: v })} /> {t('admin.campaigns.ruleMultiPersona')} <ToggleHelp tooltipKey="ruleMultiPersona" /></label>
            <label className="adm-toggle-row"><AdminToggle isOn={pDraft.multiFundamental ?? draft.multiFundamental ?? true} onChange={(v) => pPatch({ multiFundamental: v })} /> {t('admin.campaigns.ruleMultiFundamental')} <ToggleHelp tooltipKey="ruleMultiFundamental" /></label>
            <label className="adm-toggle-row"><AdminToggle isOn={pDraft.commentRequired ?? Boolean(draft.commentRequired)} onChange={(v) => pPatch({ commentRequired: v })} /> {t('admin.campaigns.commentRequired')} <ToggleHelp tooltipKey="commentRequired" /></label>
            <label className="adm-toggle-row"><AdminToggle isOn={pDraft.bulkCsvEnabled ?? draft.bulkCsvEnabled ?? true} onChange={(v) => pPatch({ bulkCsvEnabled: v })} /> {t('admin.campaigns.ruleBulkCsv')} <ToggleHelp tooltipKey="ruleBulkCsv" /></label>
            <label className="adm-toggle-row"><AdminToggle isOn={pDraft.reactionsEnabled ?? draft.reactionsEnabled ?? true} onChange={(v) => pPatch({ reactionsEnabled: v })} /> {t('admin.campaigns.ruleReactions')} <ToggleHelp tooltipKey="ruleReactions" /></label>
            <label className="adm-toggle-row adm-toggle-row--warn"><AdminToggle isOn={pDraft.sendingDisabled ?? draft.sendingDisabled ?? false} onChange={(v) => pPatch({ sendingDisabled: v })} /> {t('admin.campaigns.ruleSendingDisabled')} <ToggleHelp tooltipKey="ruleSendingDisabled" /></label>
            <label className="adm-toggle-row"><AdminToggle isOn={pDraft.permitirAnonimos ?? draft.permitirAnonimos ?? true} onChange={(v) => pPatch({ permitirAnonimos: v })} /> {t('admin.campaigns.ruleAnonymousEnabled')} <ToggleHelp tooltipKey="ruleAnonymousEnabled" /></label>
          </div>
          <p className="adm-note"><i className="fas fa-circle-info" /> {t('admin.campaigns.anonymousGestionHint')}</p>
          <div className="adm-form-grid">
            <label className="adm-field"><span>{t('admin.campaigns.ruleMinChars')}</span>
              <input type="number" min={0} max={500} step={1} className="adm-num-input--md" value={pDraft.minCommentChars ?? draft.minCommentChars ?? 20} onChange={(e) => pPatch({ minCommentChars: Math.max(0, Math.min(500, Math.round(Number(e.target.value) || 0))) })} />
            </label>
          </div>

          {/* MODERACIÓN — Edición vive aquí; Reportar/Filtro de palabras se administran en la sección Moderación.
              Bug fila 65/61: estos dos toggles escribían al GLOBAL (`patch`/`draft`) aunque rotulan la pestaña
              de Fundamentales, así que "apagar el anti-spoiler de Fundamentales" en realidad apagaba el GLOBAL
              y se colaba a Brillamos (que hereda el global sin override propio) — exactamente el síntoma de
              SIGMA. El backend YA resuelve allowPostEdit/antiSpoiler por-programa (commit b66dc63, fila 61);
              a esta pantalla nunca se le cambió el wiring. Ahora usan pDraft/pPatch como sus vecinos de arriba. */}
          <h4 className="adm-camp-fund-h">{t('admin.campaigns.moderationTitle')}</h4>
          <div className="adm-toggles-row adm-toggles-row--cards">
            <label className="adm-toggle-row">
              {/* Decisión PO 2026-09-23 — Fundamentales tiene su ajuste PROPIO, ya sin herencia de Gestión:
                  sin «restablecer a heredado». El respaldo `draft.allowPostEdit` solo pinta un doc aún no
                  rellenado por el backfill del despliegue. */}
              <AdminToggle isOn={pDraft.allowPostEdit ?? draft.allowPostEdit ?? false} onChange={(v) => pPatch({ allowPostEdit: v })} /> {t('admin.campaigns.modPostEdit')} <ToggleHelp tooltipKey="modPostEdit" />
            </label>
            <label className="adm-toggle-row">
              {/* Anti-Spoiler por campaña (2026-09-23) — el de Fundamentales cubre los reconocimientos sin
                  campaña. Nunca cierra, así que se apaga a mano (decisión 10). Sin «restablecer a
                  heredado»: sin valor, el servidor lo trata como apagado. */}
              <AdminToggle isOn={pDraft.antiSpoiler ?? false} onChange={(v) => pPatch({ antiSpoiler: v })} /> {t('admin.management.antiSpoiler')} <ToggleHelp tooltipKey="antiSpoiler" />
            </label>
          </div>
          <p className="adm-note"><i className="fas fa-circle-info" /> {t('admin.campaigns.moderationNote')}</p>

          {/* CONFIGURACIÓN DE RACHA (maqueta) */}
          <h4 className="adm-camp-fund-h">{t('admin.campaigns.streakTitle')}</h4>
          <p className="adm-note">{t('admin.campaigns.streakSentNote')}</p>
          <div className="adm-toggles-row adm-toggles-row--cards">
            <label className="adm-toggle-row"><AdminToggle isOn={draft.streakEnabled} onChange={(v) => patch({ streakEnabled: v })} /> {t('admin.campaigns.streakActive')} <ToggleHelp tooltipKey="streakActive" /></label>
            <label className="adm-toggle-row"><AdminToggle isOn={pDraft.dailyHabitsMandatory ?? draft.dailyHabitsMandatory} onChange={(v) => pPatch({ dailyHabitsMandatory: v })} /> {t('admin.management.dailyHabitsMandatory')} <ToggleHelp tooltipKey="dailyHabitsMandatory" /></label>
            <label className="adm-toggle-row"><AdminToggle isOn={draft.streakVisibleInProfile ?? true} onChange={(v) => patch({ streakVisibleInProfile: v })} /> {t('admin.campaigns.streakInProfile')} <ToggleHelp tooltipKey="streakInProfile" /></label>
            <label className="adm-toggle-row"><AdminToggle isOn={draft.badgesEnabled ?? true} onChange={(v) => patch({ badgesEnabled: v })} /> {t('admin.campaigns.badgesActive')} <ToggleHelp tooltipKey="badgesActive" /></label>
          </div>
          <div className="adm-form-grid">
            <label className="adm-field"><span>{t('admin.campaigns.streakPeriod')}</span>
              <select className="field" value={draft.periodicity} onChange={(e) => patch({ periodicity: e.target.value as GestionSettingsDto['periodicity'] })}>
                <option value="weekly">{t('admin.campaigns.periodWeekly')}</option>
                <option value="monthly">{t('admin.campaigns.periodMonthly')}</option>
              </select>
            </label>
            <label className="adm-field"><span>{t('admin.management.weeklyMinimum')}</span>
              <input type="number" min={1} max={20} step={1} className="adm-num-input" value={draft.weeklyMinimum} onChange={(e) => patch({ weeklyMinimum: Math.max(1, Math.min(20, Math.round(Number(e.target.value) || 1))) })} />
            </label>
          </div>

          {/* TOPES / GESTIÓN DE LÍMITES (maqueta) */}
          <h4 className="adm-camp-fund-h">{t('admin.campaigns.limitsTitle')}</h4>
          <div className="adm-toggles-row adm-toggles-row--cards">
            <label className="adm-toggle-row"><AdminToggle isOn={pDraft.limitsEnabled ?? draft.limitsEnabled ?? true} onChange={(v) => pPatch({ limitsEnabled: v })} /> {t('admin.campaigns.limitsActive')} <ToggleHelp tooltipKey="limitsActive" /></label>
          </div>
          <div className="adm-table-wrap">
            <table className="adm-table">
              <thead><tr>
                <th>{t('admin.campaigns.limitName')}</th>
                <th>{t('admin.campaigns.limitDescription')}</th>
                <th>{t('admin.campaigns.limitValue')}</th>
                <th>{t('admin.campaigns.limitFrequency')}</th>
                <th>{t('admin.campaigns.limitActiveCol')}</th>
              </tr></thead>
              <tbody>
                <tr>
                  <td><input type="text" className="adm-camp-tope-input" value={draft.recognitionsLimitName ?? t('admin.campaigns.limitRecognitions')} onChange={(e) => patch({ recognitionsLimitName: (e.target.value.trim() ? e.target.value : null) })} aria-label={t('admin.campaigns.limitName')} /></td>
                  <td><input type="text" className="adm-camp-tope-input" value={draft.recognitionsLimitDescription ?? t('admin.campaigns.limitRecognitionsDesc')} onChange={(e) => patch({ recognitionsLimitDescription: (e.target.value.trim() ? e.target.value : null) })} aria-label={t('admin.campaigns.limitDescription')} /></td>
                  <td><input type="number" min={1} max={1000} step={1} className="adm-num-input--md" value={pDraft.maxRecognitionsPerPeriod ?? draft.maxRecognitionsPerPeriod ?? 5} onChange={(e) => pPatch({ maxRecognitionsPerPeriod: Math.max(1, Math.min(1000, Math.round(Number(e.target.value) || 1))) })} /></td>
                  <td>
                    <select className="adm-select adm-select--sm" value={pDraft.recognitionsPeriod ?? draft.recognitionsPeriod ?? 'weekly'} onChange={(e) => pPatch({ recognitionsPeriod: e.target.value })}>
                      <option value="weekly">{t('admin.campaigns.periodWeekly')}</option>
                      <option value="monthly">{t('admin.campaigns.periodMonthly')}</option>
                      <option value="daily">{t('admin.campaigns.periodDaily')}</option>
                    </select>
                  </td>
                  <td><label className="adm-toggle-row" aria-label={t('admin.campaigns.limitActiveCol')}><AdminToggle isOn={pDraft.recognitionsLimitActive ?? draft.recognitionsLimitActive ?? true} onChange={(v) => pPatch({ recognitionsLimitActive: v })} /> <ToggleHelp tooltipKey="recognitionsLimitActive" /></label></td>
                </tr>
                <tr>
                  <td><input type="text" className="adm-camp-tope-input" value={draft.fundamentalsLimitName ?? t('admin.campaigns.limitFundamentals')} onChange={(e) => patch({ fundamentalsLimitName: (e.target.value.trim() ? e.target.value : null) })} aria-label={t('admin.campaigns.limitName')} /></td>
                  <td><input type="text" className="adm-camp-tope-input" value={draft.fundamentalsLimitDescription ?? t('admin.campaigns.limitFundamentalsDesc')} onChange={(e) => patch({ fundamentalsLimitDescription: (e.target.value.trim() ? e.target.value : null) })} aria-label={t('admin.campaigns.limitDescription')} /></td>
                  <td><input type="number" min={1} max={50} step={1} className="adm-num-input" value={pDraft.maxFundamentalsPerRecognition ?? draft.maxFundamentalsPerRecognition ?? 2} onChange={(e) => pPatch({ maxFundamentalsPerRecognition: Math.max(1, Math.min(50, Math.round(Number(e.target.value) || 1))) })} /></td>
                  <td>{t('admin.campaigns.perSubmission')}</td>
                  <td><label className="adm-toggle-row" aria-label={t('admin.campaigns.limitActiveCol')}><AdminToggle isOn={pDraft.fundamentalsLimitActive ?? draft.fundamentalsLimitActive ?? true} onChange={(v) => pPatch({ fundamentalsLimitActive: v })} /> <ToggleHelp tooltipKey="fundamentalsLimitActive" /></label></td>
                </tr>
              </tbody>
            </table>
          </div>

          <AdminSaveBar onSave={doSave} saving={savingAny} dirty={anyDirty} saved={savedAny} />
          {(save.isError || pSave.isError) && <p className="adm-error" role="alert"><i className="fas fa-triangle-exclamation" /> {getRecognitionErrorMessage(save.error ?? pSave.error, t('admin.saveError'))}</p>}
        </>)}

        {/* Vista Admin OU/Embajador — config general en SOLO LECTURA (la fija el Admin Global). */}
        {draft && pDraft && vista === 'ou' && (
          <div className="adm-camp-readonly">
            <div className="adm-camp-readonly-head">
              <i className="fas fa-lock" aria-hidden="true" />
              <span>{t('admin.campaigns.readonlyTitle')}</span>
              <span className="adm-camp-readonly-badge">{t('admin.campaigns.readonlyBadge')}</span>
            </div>
            <div className="adm-camp-readonly-grid">
              <div><strong>{t('admin.campaigns.entryPoints')}</strong><span>{t(`admin.campaigns.entry.${pDraft.entryPointMode ?? draft.entryPointMode ?? 'A'}`)}</span></div>
              <div><strong>{t('admin.campaigns.rulesTitle')}</strong><span>{[(pDraft.multinomination ?? draft.multinomination) && t('admin.campaigns.ruleMultiPersona'), (pDraft.commentRequired ?? draft.commentRequired) && t('admin.campaigns.commentRequired'), (pDraft.reactionsEnabled ?? draft.reactionsEnabled) && t('admin.campaigns.ruleReactions')].filter(Boolean).join(' · ') || '—'}</span></div>
              <div><strong>{t('admin.campaigns.moderationTitle')}</strong><span>{[draft.allowPostEdit && t('admin.campaigns.modPostEdit')].filter(Boolean).join(' · ') || '—'}</span></div>
              <div><strong>{t('admin.campaigns.streakTitle')}</strong><span>{draft.streakEnabled ? `${periodLabel(draft.periodicity)} · ${t('admin.management.weeklyMinimum')}: ${draft.weeklyMinimum}` : '—'}</span></div>
              <div><strong>{t('admin.campaigns.limitsTitle')}</strong><span>{`${pDraft.maxRecognitionsPerPeriod ?? draft.maxRecognitionsPerPeriod ?? 5} · ${pDraft.maxFundamentalsPerRecognition ?? draft.maxFundamentalsPerRecognition ?? 2} ${t('admin.campaigns.limitFundamentals')}`}</span></div>
            </div>
            <p className="adm-camp-readonly-note"><i className="fas fa-circle-info" /> {t('admin.campaigns.readonlyNote')}</p>
          </div>
        )}
      </StateBlock>

      <p className="adm-note"><i className="fas fa-circle-info" /> {t('admin.campaigns.fundConfigNote')}</p>

      {/* CPE-138 — Campañas por Localidad (excepciones): tabla + aprobaciones + asistente de 3 pasos,
          debajo de la configuración de Fundamentales (config → Guardar → Campañas por Localidad).
          `isReadOnly` cuando la Vista está en Admin OU/Embajador — ver doc-comment de la prop en
          `CampaignExceptions.tsx`: esa vista es una simulación de solo lectura y no debe ofrecer
          "+ Nueva excepción por OU". */}
      <CampaignExceptionsPanel isReadOnly={vista === 'ou'} />
    </div>
  );
}

/**
 * WP — Reglas de envío PROPIAS de Brillamos (modelo por-programa, `recognition_program_settings`).
 * Cada control muestra el valor EFECTIVO (propio del programa si está definido; si no, el global de
 * Gestión) y, al tocarlo, escribe un override propio de Brillamos. Un campo nunca tocado queda `null`
 * = hereda el global. Detrás de un feature flag de dev (ver montaje) hasta que se libere. Solo AdminGlobal.
 */
function BrillamosRulesPanel() {
  const { t } = useTranslation('recognition');
  const query = useProgramSettings('Brillamos');
  const globalQ = useGestionSettings();
  const save = useSaveProgramSettings('Brillamos');
  const [draft, setDraft] = useState<RecognitionProgramSettingsDto | null>(null);
  const [dirty, setDirty] = useState(false);
  useEffect(() => {
    setDraft((cur) => (query.data && (cur === null || !dirty) ? query.data : cur));
  }, [query.data, dirty]);
  const patch = (p: Partial<RecognitionProgramSettingsDto>) => { setDraft((d) => (d ? { ...d, ...p } : d)); setDirty(true); };
  const doSave = () => { if (draft) save.mutate(draft, { onSuccess: () => setDirty(false) }); };
  const g = globalQ.data;
  const clampInt = (raw: string, lo: number, hi: number) => Math.max(lo, Math.min(hi, Math.round(Number(raw) || lo)));

  return (
    <div className="adm-camp-fund adm-camp-brillamos-rules">
      <StateBlock isLoading={query.isLoading} isError={query.isError} error={query.error}
        isEmpty={!draft} emptyText={t('common.empty')} onRetry={() => { query.refetch(); }}>
        {draft && (<>
          <p className="adm-note"><i className="fas fa-circle-info" /> {t('admin.campaigns.brillamosRulesIntro')}</p>

          {/* Pedido 2026-09-29 — la modalidad (entry points) salió de aquí: es de cada campaña (paso 5 del
              asistente). El doc de Brillamos conserva `entryPointMode` solo como valor heredado para
              campañas sin valor propio; se reenvía tal cual al guardar. */}
          <h4 className="adm-camp-fund-h">{t('admin.campaigns.entryPoints')}</h4>
          <p className="adm-note"><i className="fas fa-pen-to-square" /> {t('admin.campaigns.entryPointsPerCampaignNote')}</p>

          <h4 className="adm-camp-fund-h">{t('admin.campaigns.rulesTitle')}</h4>
          <div className="adm-toggles-row adm-toggles-row--cards">
            <label className="adm-toggle-row"><AdminToggle isOn={draft.commentRequired ?? g?.commentRequired ?? false} onChange={(v) => patch({ commentRequired: v })} /> {t('admin.campaigns.commentRequired')} <ToggleHelp tooltipKey="commentRequired" /></label>
            <label className="adm-toggle-row"><AdminToggle isOn={draft.bulkCsvEnabled ?? g?.bulkCsvEnabled ?? true} onChange={(v) => patch({ bulkCsvEnabled: v })} /> {t('admin.campaigns.ruleBulkCsv')} <ToggleHelp tooltipKey="ruleBulkCsv" /></label>
            <label className="adm-toggle-row"><AdminToggle isOn={draft.reactionsEnabled ?? g?.reactionsEnabled ?? true} onChange={(v) => patch({ reactionsEnabled: v })} /> {t('admin.campaigns.ruleReactions')} <ToggleHelp tooltipKey="ruleReactions" /></label>
            <label className="adm-toggle-row adm-toggle-row--warn"><AdminToggle isOn={draft.sendingDisabled ?? g?.sendingDisabled ?? false} onChange={(v) => patch({ sendingDisabled: v })} /> {t('admin.campaigns.ruleSendingDisabled')} <ToggleHelp tooltipKey="ruleSendingDisabled" /></label>
            <label className="adm-toggle-row"><AdminToggle isOn={draft.permitirAnonimos ?? g?.permitirAnonimos ?? true} onChange={(v) => patch({ permitirAnonimos: v })} /> {t('admin.campaigns.ruleAnonymousEnabled')} <ToggleHelp tooltipKey="ruleAnonymousEnabled" /></label>
          </div>
          {/* Pedido PO 2026-09-29 — Multi-persona, Multi-fundamental y «Fundamentales por reco» salieron
              de aquí: son de cada campaña (paso 5 del asistente). Los valores de este doc siguen siendo lo
              que hereda una campaña que no define los suyos, y de donde sale el relleno de despliegue. */}
          <p className="adm-note"><i className="fas fa-users" /> {t('admin.campaigns.multiEnvioPerCampaignNote')}</p>
          <div className="adm-form-grid">
            <label className="adm-field"><span>{t('admin.campaigns.ruleMinChars')}</span>
              <input type="number" min={0} max={500} step={1} value={draft.minCommentChars ?? g?.minCommentChars ?? 20} onChange={(e) => patch({ minCommentChars: clampInt(e.target.value, 0, 500) })} />
            </label>
          </div>

          {/* MODERACIÓN — decisión PO 2026-09-23: ni la edición post-envío ni el Modo Anti-Spoiler viven
              ya en el doc de Brillamos. Son de cada campaña (paso 5 del asistente) y el servidor no lee
              los de este doc; aquí solo queda dónde encontrarlos. */}
          <h4 className="adm-camp-fund-h">{t('admin.campaigns.moderationTitle')}</h4>
          <p className="adm-note"><i className="fas fa-pen" /> {t('admin.campaigns.postEditPerCampaignNote')}</p>
          <p className="adm-note"><i className="fas fa-eye-slash" /> {t('admin.campaigns.antiSpoilerPerCampaignNote')}</p>
          <p className="adm-note"><i className="fas fa-circle-info" /> {t('admin.campaigns.moderationNote')}</p>

          {/* CONFIGURACIÓN DE RACHA — override PROPIO de Brillamos (doc de programa). El motor calcula la
              racha de Brillamos POR SEPARADO de Fundamentales (Rama B, tras el kill-switch
              Recognition:ProgramStreaksEnabled). Efectivo = programa ?? global; vacío = hereda Gestión. */}
          <h4 className="adm-camp-fund-h">{t('admin.campaigns.streakTitle')}</h4>
          <p className="adm-note">{t('admin.campaigns.streakSentNote')}</p>
          <div className="adm-toggles-row adm-toggles-row--cards">
            <label className="adm-toggle-row"><AdminToggle isOn={draft.streakEnabled ?? g?.streakEnabled ?? true} onChange={(v) => patch({ streakEnabled: v })} /> {t('admin.campaigns.streakActive')} <ToggleHelp tooltipKey="streakActive" /></label>
            <label className="adm-toggle-row"><AdminToggle isOn={draft.dailyHabitsMandatory ?? g?.dailyHabitsMandatory ?? false} onChange={(v) => patch({ dailyHabitsMandatory: v })} /> {t('admin.management.dailyHabitsMandatory')} <ToggleHelp tooltipKey="dailyHabitsMandatory" /></label>
            <label className="adm-toggle-row"><AdminToggle isOn={draft.streakVisibleInProfile ?? g?.streakVisibleInProfile ?? true} onChange={(v) => patch({ streakVisibleInProfile: v })} /> {t('admin.campaigns.streakInProfile')} <ToggleHelp tooltipKey="streakInProfile" /></label>
            <label className="adm-toggle-row"><AdminToggle isOn={draft.badgesEnabled ?? g?.badgesEnabled ?? true} onChange={(v) => patch({ badgesEnabled: v })} /> {t('admin.campaigns.badgesActive')} <ToggleHelp tooltipKey="badgesActive" /></label>
          </div>
          <div className="adm-form-grid">
            <label className="adm-field"><span>{t('admin.campaigns.streakPeriod')}</span>
              <select className="field" value={draft.periodicity ?? g?.periodicity ?? 'weekly'} onChange={(e) => patch({ periodicity: e.target.value })}>
                <option value="weekly">{t('admin.campaigns.periodWeekly')}</option>
                <option value="monthly">{t('admin.campaigns.periodMonthly')}</option>
              </select>
            </label>
            <label className="adm-field"><span>{t('admin.management.weeklyMinimum')}</span>
              <input type="number" min={1} max={20} step={1} value={draft.weeklyMinimum ?? g?.weeklyMinimum ?? 1} onChange={(e) => patch({ weeklyMinimum: clampInt(e.target.value, 1, 20) })} />
            </label>
          </div>

          <h4 className="adm-camp-fund-h">{t('admin.campaigns.limitsTitle')}</h4>
          <div className="adm-toggles-row adm-toggles-row--cards">
            <label className="adm-toggle-row"><AdminToggle isOn={draft.limitsEnabled ?? g?.limitsEnabled ?? true} onChange={(v) => patch({ limitsEnabled: v })} /> {t('admin.campaigns.limitsActive')} <ToggleHelp tooltipKey="limitsActive" /></label>
            <label className="adm-toggle-row"><AdminToggle isOn={draft.recognitionsLimitActive ?? g?.recognitionsLimitActive ?? true} onChange={(v) => patch({ recognitionsLimitActive: v })} /> {t('admin.campaigns.limitRecognitions')} <ToggleHelp tooltipKey="recognitionsLimitActive" /></label>
          </div>
          <div className="adm-form-grid">
            <label className="adm-field"><span>{t('admin.campaigns.limitRecognitions')}</span>
              <input type="number" min={1} max={1000} step={1} value={draft.maxRecognitionsPerPeriod ?? g?.maxRecognitionsPerPeriod ?? 5} onChange={(e) => patch({ maxRecognitionsPerPeriod: clampInt(e.target.value, 1, 1000) })} />
            </label>
            <label className="adm-field"><span>{t('admin.campaigns.limitFrequency')}</span>
              <select className="field" value={draft.recognitionsPeriod ?? g?.recognitionsPeriod ?? 'weekly'} onChange={(e) => patch({ recognitionsPeriod: e.target.value })}>
                <option value="weekly">{t('admin.campaigns.periodWeekly')}</option>
                <option value="monthly">{t('admin.campaigns.periodMonthly')}</option>
                <option value="daily">{t('admin.campaigns.periodDaily')}</option>
              </select>
            </label>
          </div>

          <p className="adm-note"><i className="fas fa-circle-info" /> {t('admin.campaigns.fundConfigNote')}</p>
          <AdminSaveBar onSave={doSave} saving={save.isPending} dirty={dirty} saved={save.isSuccess} />
          {save.isError && <p className="adm-error" role="alert"><i className="fas fa-triangle-exclamation" /> {getRecognitionErrorMessage(save.error, t('admin.saveError'))}</p>}
        </>)}
      </StateBlock>
    </div>
  );
}

/**
 * Banner PROPIO de Brillamos (subtítulo + logo por idioma) — réplica de la maqueta, EDITABLE (el cliente
 * eligió "banner editable", sobre REQ-034). Cada programa lleva su banner: este es el de Brillamos, no el
 * de Fundamentales. Vive en el MISMO documento de Gestión (GestionSettings.brillamosBanner*), por eso
 * carga y GUARDA el borrador COMPLETO — el PUT reemplaza el doc entero y un guardado parcial pisaría en
 * silencio la config de Fundamentales/badges/racha (mismo cuidado que FundamentalesPanel). Solo el Admin
 * Global fija la identidad del programa. Lo consume /app/brillamos vía el GET no-admin del landing.
 */
export function BrillamosBannerEditor() {
  const { t } = useTranslation('recognition');
  const query = useGestionSettings();
  const save = useSaveGestionSettings();
  const [draft, setDraft] = useState<GestionSettingsDto | null>(null);
  const [dirty, setDirty] = useState(false);
  useEffect(() => {
    setDraft((cur) => (query.data && (cur === null || !dirty) ? query.data : cur));
  }, [query.data, dirty]);
  const patch = (p: Partial<GestionSettingsDto>) => { setDraft((d) => (d ? { ...d, ...p } : d)); setDirty(true); };
  const doSave = () => { if (draft) save.mutate(draft, { onSuccess: () => setDirty(false) }); };

  // El gate de rol (Admin Global) va en el punto de montaje para no disparar el GET de Gestión a quien
  // no puede editar; aquí el componente asume que ya pasó.
  return (
    <div className="adm-camp-fund adm-camp-brillamos-banner">
      <h4 className="adm-camp-fund-h">{t('admin.campaigns.brillamosBannerTitle')}</h4>
      <p className="adm-note">{t('admin.campaigns.brillamosBannerHint')}</p>
        <StateBlock
          isLoading={query.isLoading}
          isError={query.isError}
          error={query.error}
          isEmpty={!draft}
          emptyText={t('common.empty')}
          onRetry={() => { query.refetch(); }}
        >
          {draft && (<>
            {/* Formulario "subtítulo por idioma + logo por idioma + preview" compartido con el paso
                Banner del wizard de excepciones (Tarea 14) — ver `BannerEditorFields` en adminShared.tsx. */}
            <BannerEditorFields
              value={{ subtitle: draft.brillamosBannerSubtitle ?? {}, logoUrl: draft.brillamosBannerLogoUrl ?? {} }}
              onChange={(next) => patch({ brillamosBannerSubtitle: next.subtitle, brillamosBannerLogoUrl: next.logoUrl })}
            />

            {/* La modalidad (entry points) de Brillamos se movió al PANEL DE REGLAS de Brillamos, que
                escribe el doc de programa (entryPointMode). Aquí solo queda el diseño del banner. */}
            <AdminSaveBar onSave={doSave} saving={save.isPending} dirty={dirty} saved={save.isSuccess} />
            {save.isError && <p className="adm-error" role="alert"><i className="fas fa-triangle-exclamation" /> {getRecognitionErrorMessage(save.error, t('admin.saveError'))}</p>}
          </>)}
        </StateBlock>
    </div>
  );
}

// ── Config INLINE de Brillamos (1:1 maqueta): tarjeta Status + fila Fecha/Zona/Aplican puntos + tabla
// "Horarios y Fechas por País". Edita la campaña Brillamos ACTIVA (o la primera) vía PATCH campaigns.
// La grilla por-país se persiste en Campaign.HorariosPorPais y el backend la enforcea (fecha + horario
// diario por zona). Los países son las OUs top-level (parentOuId == null); su timezone alimenta el default.
/** Estado que se pinta en la última columna: sale de la campaña REAL del país, no de un valor por omisión. */
/** Sin horario diario configurado la campaña corre todo el día dentro de sus fechas. */
const HORA_DIA_COMPLETO_INICIO = '00:00';
const HORA_DIA_COMPLETO_FIN = '23:59';

type PaisEstadoClave = 'activo' | 'detenido' | 'fueraDeVentana' | 'porIniciar' | 'sinCampana';

interface PaisRow {
  fechaInicio: string; fechaFin: string; horaInicio: string; horaFin: string; zonaHoraria: string;
  /** Nombre (en el idioma de la pantalla) de la campaña que gobierna al país; vacío si no hay. */
  campanaNombre: string;
  estado: PaisEstadoClave;
}

/**
 * Bug UAT 20-ago-2026 — «la tabla no refleja el horario que se define desde una Campaña de OU (que toma
 * preferencia)». Lo que una Campaña de OU (excepción por localidad APROBADA + activa) impone sobre un país.
 * El backend ya le daba preferencia al ENVIAR (`BrillamosScheduleGate` compone la grilla por país CON las
 * excepciones y exige cumplir las dos), pero esta tabla solo pintaba la grilla, así que el administrador no
 * tenía forma de ver que su fila estaba siendo pisada.
 */
interface PaisOverride {
  /** Id de la excepción: llave estable de la subfila. */
  id: string;
  /** Alcance legible de la excepción ("México › Carnes Frías › 2 sites"). */
  etiqueta: string;
  envioDesactivado: boolean;
  fechaInicio: string;
  fechaFin: string;
  horaInicio: string;
  horaFin: string;
  zonaHoraria: string;
}

/**
 * Quita el país del principio del alcance legible de una campaña de OU («México › Planta Toluca» → «Planta Toluca»).
 * El país ya va en la primera columna de la subfila (QA 19-oct-2026); repetirlo en el alcance sería ruido. Compara sin
 * acentos ni mayúsculas, con el nombre del catálogo y con el que se pinta (el catálogo trae «MEXICO», la
 * pantalla «México»). Si el alcance no empieza por el país, se deja tal cual.
 */
function sinPais(nombresDelPais: readonly string[], alcance: string): string {
  const normalizado = normalizarNombreOu(alcance);
  for (const nombre of nombresDelPais) {
    const prefijo = `${normalizarNombreOu(nombre)} › `;
    if (normalizado.startsWith(prefijo)) return alcance.slice(prefijo.length);
  }
  return alcance;
}

/**
 * País (OU top-level) → TODAS las Campañas de OU activas que le aplican y dicen algo sobre horarios.
 *
 * El alcance de una excepción se persiste EXPANDIDO (ver `expandOuIds`), así que una excepción creada para
 * una OU POR DEBAJO del país NO contiene el id del país: hay que comparar contra el SUBÁRBOL completo del
 * país, no contra su id.
 *
 * 🔴 Son TODAS, no la primera (QA 19-oct-2026: la tabla debe reflejar «las campañas de cada OU»). El
 * anti-solape al aprobar (`HasActiveOverlapAsync`) solo impide dos campañas sobre la MISMA geografía; dos
 * plantas distintas del mismo país, cada una con su campaña, no se solapan y las dos son válidas. Quedarse
 * con la primera escondía la segunda a quien consulta la tabla para saber cuándo puede reconocer en esa planta.
 */
function buildPaisOverrides(
  countries: readonly OUItem[], items: OUItem[], excepciones: readonly CampaignExceptionDto[],
): Record<string, PaisOverride[]> {
  const out: Record<string, PaisOverride[]> = {};
  for (const c of countries) {
    const subarbol = new Set(expandOuIds([c.id], items));
    const delPais: PaisOverride[] = [];
    for (const exc of excepciones) {
      if (!exc.activa || exc.estadoAprobacion !== 'aprobada') continue;
      if (!exc.alcance.some((id) => subarbol.has(id))) continue;
      const o = exc.override ?? {};
      // Solo cuenta si dice algo de HORARIOS: una excepción de tope o de visibilidad no pisa esta tabla.
      const habla = o.desactivarEnvio === true
        || Boolean(o.fechaInicio) || Boolean(o.fechaFin) || Boolean(o.horaInicio) || Boolean(o.horaFin);
      if (!habla) continue;
      delPais.push({
        id: exc.id,
        etiqueta: sinPais([c.name, metaDeOu(c.name).label], exc.alcanceEtiqueta || ouLabels(collapseOuIds(exc.alcance, items), items)),
        envioDesactivado: o.desactivarEnvio === true,
        fechaInicio: o.fechaInicio ? o.fechaInicio.slice(0, 10) : '',
        fechaFin: o.fechaFin ? o.fechaFin.slice(0, 10) : '',
        horaInicio: o.horaInicio ?? '',
        horaFin: o.horaFin ?? '',
        zonaHoraria: o.zonaHoraria ?? '',
      });
    }
    // Orden estable por alcance legible: la API no garantiza uno.
    if (delPais.length > 0) out[c.id] = delPais.sort((a, b) => a.etiqueta.localeCompare(b.etiqueta));
  }
  return out;
}

/**
 * Nombre de la OU raíz («MEXICO» del catálogo → «México»). Sin bandera (QA 20-oct-2026): en Windows los emoji de
 * bandera se ven como letras («MX», «US») y el globo genérico de las OUs sin bandera no aporta nada.
 */
function PaisNombre({ nombre }: { nombre: string }) {
  return <>{metaDeOu(nombre).label}</>;
}

/** Estado de una fila de la tabla informativa. Solo texto: no es un control. */
function PaisEstado({ estado }: { estado: PaisEstadoClave }) {
  const { t } = useTranslation('recognition');
  const textos: Record<PaisEstadoClave, string> = {
    activo: t('admin.campaigns.paisStateOn'),
    detenido: t('admin.campaigns.paisStateOff'),
    fueraDeVentana: t('admin.campaigns.status.ActivaVencida'),
    porIniciar: t('admin.campaigns.status.ActivaPorIniciar'),
    sinCampana: t('admin.campaigns.paisStateNoCampaign'),
  };
  // Verde solo si la campaña corre de verdad; ámbar si está activa pero fuera de su ventana (mismo criterio
  // que el listado de campañas); gris si no hay nada gobernando ese país.
  const clase = estado === 'activo' ? 'adm-status adm-status--on'
    : estado === 'fueraDeVentana' || estado === 'porIniciar' ? 'adm-status adm-status--warn'
      : 'adm-status adm-status--off';
  const icono = estado === 'activo' ? 'fa-check' : estado === 'fueraDeVentana' || estado === 'porIniciar' ? 'fa-clock' : 'fa-xmark';
  return (
    <span className={clase}>
      <i className={`fas ${icono}`} aria-hidden="true" />{' '}
      {textos[estado]}
    </span>
  );
}

/**
 * Config inline de la campaña Brillamos ACTIVA.
 *
 * 🔴 Bug UAT 20-ago-2026 — antes recibía por prop la lista que pinta la tabla de abajo
 * (`useCampaigns(status)`, **página 1 de 20**, filtrada además por el chip de estado) y resolvía
 * `campaigns.find(estado === 'Activa') ?? campaigns[0]`. Dos formas de escribir en la campaña
 * EQUIVOCADA, las dos silenciosas: con el chip en cualquier estado que no fuera «Todas»/«Activa», o
 * con la Activa fuera de la primera página de 20 (en QA hay más de 50 campañas), el `?? campaigns[0]`
 * mandaba el horario a la primera de la lista — típicamente una Finalizada, donde no aplica nada.
 * Medido en QA el 21-ago: 4 campañas FINALIZADAS con filas de horario guardadas.
 *
 * Ahora pide ella misma las campañas en estado `Activa` (donde hay un puñado, no decenas) y si no
 * hay ninguna lo DICE, en vez de editar otra.
 */
interface BrillamosConfigInlineProps {
  /**
   * La tabla «Horarios y Fechas por País» se ocultó el 22-ago-2026 (UAT) mientras era editable. QA pidió
   * (16-oct-2026) volver a mostrarla pero como tabla **informativa**: refleja los tiempos que definen las
   * Campañas de OU activas, sin botones para editar ni para detener (ni toggles, ni Guardar). Es lo que
   * hace ahora; la prop sigue existiendo solo para que las pruebas puedan apagarla.
   */
  mostrarHorariosPorPais?: boolean;
}

/**
 * Campaña Brillamos ACTIVA que gobierna a un país: la que cubre su subárbol de OUs. En QA hay una campaña por
 * país (cada una con sus propias fechas y zona; la grilla `horariosPorPais` suele ir vacía), así que las
 * fechas y la zona de la fila salen de ELLA. Si varias lo cubren, gana la vigente y, si no, la que termina
 * más tarde.
 */
function campanaDelPais(
  campanas: readonly CampaignDto[], subarbol: ReadonlySet<string>, ahora: Date,
): CampaignDto | null {
  const candidatas = campanas.filter((c) => c.alcance.some((id) => subarbol.has(id)));
  if (candidatas.length === 0) return null;
  const finDe = (c: CampaignDto) => (c.fechaFin ? new Date(c.fechaFin).getTime() : Infinity);
  return [...candidatas].sort((a, b) => {
    const va = vigenciaDeCampana(a, ahora) === 'vigente' ? 1 : 0;
    const vb = vigenciaDeCampana(b, ahora) === 'vigente' ? 1 : 0;
    return vb - va || finDe(b) - finDe(a);
  })[0];
}

export function BrillamosConfigInline({ mostrarHorariosPorPais = true }: BrillamosConfigInlineProps = {}) {
  const { t, i18n } = useTranslation('recognition');
  const lang = i18n.language.split('-')[0];
  const { data: ouItems = [] } = useOUItems();
  // Un solo endpoint de SOLO LECTURA con las campañas Brillamos ACTIVAS y las campañas por OU aprobadas
  // y vigentes, de TODAS las OUs. No se usan `useCampaigns`/`useCampaignExceptions` a propósito: el
  // backend los acota a la cascada del Admin OU, y esta tabla es informativa — el Admin OU necesita los
  // lapsos de todas las OUs para saber cuándo se puede reconocer cross-OU (QA 16-oct-2026). El conjunto
  // llega COMPLETO (sin paginar): paginar dejaría países sin marcar en silencio.
  const overview = useCampaignSchedulesOverview();
  const excepciones = overview.data?.exceptions;

  const activas = useMemo(
    () => (overview.data?.campaigns ?? []).filter((c) => c.tipo === 'Brillamos' && c.estado === 'Activa'),
    [overview.data],
  );
  // La tarjeta y el resumen de arriba hablan de UNA campaña: solo tienen sentido si hay una. Con una campaña
  // por país (lo habitual en QA) eligir una al azar sería engañoso; el detalle por país está en la tabla.
  const active = activas.length === 1 ? activas[0] : null;
  const countries = useMemo(() => ouItems.filter((o) => !o.parentOuId), [ouItems]);
  const overrides = useMemo(
    () => buildPaisOverrides(countries, ouItems, excepciones ?? []),
    [countries, ouItems, excepciones],
  );

  // Valores que muestra la fila de cada país: la grilla guardada en la campaña Brillamos. Las Campañas de OU
  // activas NO se mezclan aquí: cada una va en su propia subfila (ver el render). Solo lectura.
  const rows = useMemo(() => {
    const map: Record<string, PaisRow> = {};
    const ahora = new Date();
    for (const c of countries) {
      const campana = campanaDelPais(activas, new Set(expandOuIds([c.id], ouItems)), ahora);
      const s = campana?.horariosPorPais?.find((h) => h.ouId === c.id);
      let estado: PaisEstadoClave = 'sinCampana';
      if (campana) {
        const vigencia = vigenciaDeCampana(campana, ahora);
        if (s?.activo === false) estado = 'detenido';
        else if (vigencia === 'vencida') estado = 'fueraDeVentana';
        else if (vigencia === 'porIniciar') estado = 'porIniciar';
        else estado = 'activo';
      }
      // Lo de la grilla manda; si no hay, las fechas y la zona de la campaña. Sin horario diario configurado el
      // backend no restringe (`CampaignScheduleEvaluator`: sin fila ⇒ solo rige la ventana de fechas de la
      // campaña), o sea que corre TODO el día: se muestra 00:00–23:59 en vez de guiones que parecían un dato
      // que faltaba. La columna «Aplicar horario» se retiró: era el interruptor de la grilla editable y en una
      // tabla informativa decía «No» junto a fechas que sí se aplican.
      map[c.id] = {
        campanaNombre: campana ? getCampaignName(campana, lang) : '',
        fechaInicio: (s?.fechaInicio || campana?.fechaInicio || '').slice(0, 10),
        fechaFin: (s?.fechaFin || campana?.fechaFin || '').slice(0, 10),
        horaInicio: s?.horaInicio || (campana ? HORA_DIA_COMPLETO_INICIO : ''),
        horaFin: s?.horaFin || (campana ? HORA_DIA_COMPLETO_FIN : ''),
        zonaHoraria: s?.zonaHoraria || campana?.zonaHoraria || c.timezone || DEFAULT_TZ,
        // Aditivo: las filas guardadas antes de que existiera el campo aplicaban su ventana.
        estado,
      };
    }
    return map;
  }, [countries, ouItems, activas, lang]);

  // Sin campaña Brillamos ACTIVA no hay horarios que configurar, y hay que decirlo: callarse es lo
  // que llevaba a capturar horarios en una campaña finalizada creyendo que se estaban aplicando.
  if (overview.isLoading) return null;
  if (activas.length === 0) {
    return (
      <div className="adm-camp-fund adm-brillamos-config">
        <p className="adm-note"><i className="fas fa-circle-info" aria-hidden="true" /> {t('admin.campaigns.paisScheduleNoActive')}</p>
      </div>
    );
  }

  return (
    <div className="adm-camp-fund adm-brillamos-config">
      {active && (<>
      {/* Tarjeta de estado de la campaña recurrente */}
      <div className="adm-camp-active-card">
        <i className="fas fa-star" aria-hidden="true" />
        <div>
          <p className="adm-camp-active-title">{getCampaignName(active, lang)}</p>
          <p className="adm-camp-active-desc">{t('admin.campaigns.brillamosRecurringDesc')}</p>
        </div>
        <span className={statusBadge(active.estado)}>{t(`admin.campaigns.status.${active.estado}`)}</span>
      </div>

      {/* Fila resumen: Fecha inicio/fin · Zona · Aplican puntos (a nivel campaña; se editan en el wizard) */}
      <div className="adm-brillamos-summary">
        <div><span className="adm-summary-lbl">{t('admin.campaigns.fieldStart')}</span><span>{active.fechaInicio ? active.fechaInicio.slice(0, 10) : '—'}</span></div>
        <div><span className="adm-summary-lbl">{t('admin.campaigns.fieldEnd')}</span><span>{active.fechaFin ? active.fechaFin.slice(0, 10) : '—'}</span></div>
        <div><span className="adm-summary-lbl">{t('admin.campaigns.fieldTimezone')}</span><span>{active.zonaHoraria}</span></div>
        <div><span className="adm-summary-lbl">{t('admin.campaigns.applyPoints')}</span><span className={active.visibilidad.aplicanPuntos ? 'adm-status adm-status--on' : 'adm-status adm-status--off'}><i className={`fas ${active.visibilidad.aplicanPuntos ? 'fa-check' : 'fa-xmark'}`} aria-hidden="true" /></span></div>
      </div>
      </>)}

      {mostrarHorariosPorPais && (<>
        {/* Horarios y Fechas por País — INFORMATIVA (QA 16-oct-2026): sin edición ni acciones. */}
        <h4 className="adm-camp-fund-h">{t('admin.campaigns.paisScheduleTitle')}</h4>
        <p className="adm-note">{t('admin.campaigns.paisScheduleInfoIntro')}</p>
        <div className="adm-table-wrap">
          <table className="adm-table adm-table--glass">
            <thead>
              <tr>
                <th>{t('admin.campaigns.paisCol')}</th>
                <th>{t('admin.campaigns.fieldStart')}</th>
                <th>{t('admin.campaigns.fieldEnd')}</th>
                <th>{t('admin.campaigns.paisOpen')}</th>
                <th>{t('admin.campaigns.paisClose')}</th>
                <th>{t('admin.campaigns.fieldTimezone')}</th>
                <th>{t('admin.campaigns.paisActive')}</th>
              </tr>
            </thead>
            <tbody>
              {countries.map((c) => {
                const r = rows[c.id] ?? { fechaInicio: '', fechaFin: '', horaInicio: '', horaFin: '', zonaHoraria: c.timezone || DEFAULT_TZ, campanaNombre: '', estado: 'sinCampana' as const };
                const campanasDeOu = overrides[c.id] ?? [];
                return (
                  <Fragment key={c.id}>
                    <tr>
                      <td>
                        <PaisNombre nombre={c.name} />
                        {r.campanaNombre && <small className="adm-pais-campana">{r.campanaNombre}</small>}
                      </td>
                      <td>{r.fechaInicio || '—'}</td>
                      <td>{r.fechaFin || '—'}</td>
                      <td>{r.horaInicio || '—'}</td>
                      <td>{r.horaFin || '—'}</td>
                      <td>{r.zonaHoraria}</td>
                      <td><PaisEstado estado={r.estado} /></td>
                    </tr>
                    {/* Una subfila por Campaña de OU activa que aplica al país: sus propios tiempos, sin mezclarlos
                        con los de la fila del país (QA 19-oct-2026: «las campañas de cada OU activas»). */}
                    {campanasDeOu.map((ov) => (
                      <tr key={ov.id} className="adm-pais-row--overridden">
                        <td>
                          <PaisNombre nombre={c.name} />
                          <span className="adm-pais-override">
                            <span className="adm-badge adm-badge--warn">
                              <i className="fas fa-code-branch" aria-hidden="true" /> {t('admin.campaigns.paisOverrideBadge')}
                            </span>
                            <small>{ov.etiqueta}</small>
                          </span>
                        </td>
                        <td>{ov.fechaInicio || '—'}</td>
                        <td>{ov.fechaFin || '—'}</td>
                        <td>{ov.horaInicio || '—'}</td>
                        <td>{ov.horaFin || '—'}</td>
                        <td>{ov.zonaHoraria || r.zonaHoraria}</td>
                        <td><PaisEstado estado={ov.envioDesactivado ? 'detenido' : 'activo'} /></td>
                      </tr>
                    ))}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </>)}
    </div>
  );
}

export function AdminCampaigns() {
  const { t, i18n } = useTranslation('recognition');
  const lang = i18n.language.split('-')[0];
  const [mode, setMode] = useState<Mode>({ kind: 'list' });
  const [campType, setCampType] = useState<CampType>('fundamentales');
  // Chips de Personalizadas (estado guardado) y de Brillamos (estado visible, ADR 0014): cada pestaña
  // conserva el suyo.
  const [status, setStatus] = useState<CampaignStatus | 'all'>('all');
  const [filtroBrillamos, setFiltroBrillamos] = useState<CampaignEstadoVisible | 'all'>('all');
  const [panicConfirm, setPanicConfirm] = useState<PanicConfirm | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<CampaignDto | null>(null);
  // Bug UAT #114 — `ouProgram` (Tarea 7) se retiró: el rol OU-acotado ya no tiene selector propio ni
  // pantalla aparte, usa las mismas tabs que el resto (solo sin «Personalizadas»), así que `campType`
  // vuelve a ser el único estado de programa. Mantener dos en paralelo se desincronizaba en cuanto las
  // dos superficies compartían componentes.

  // Brillamos con un chip elegido → el servidor filtra por estado visible. «Todas» (y Personalizadas)
  // siguen usando el listado de siempre; `status` solo aplica a Personalizadas.
  const filtraPorVisible = campType === 'brillamos' && filtroBrillamos !== 'all';
  const statusLista = campType === 'personalizadas' && status !== 'all' ? status : undefined;
  const campaignsPorStatus = useCampaigns(statusLista, 1, 20, !filtraPorVisible);
  const campaignsPorVisible = useBrillamosCampaignsByEstadoVisible(
    filtroBrillamos === 'all' ? undefined : filtroBrillamos, 1, 20, filtraPorVisible);
  const campaigns = filtraPorVisible ? campaignsPorVisible : campaignsPorStatus;
  const duplicateM = useDuplicateCampaign();
  const panicM = usePanicCampaign();
  const undoPanicM = useUndoPanicCampaign();
  const deleteM = useDeleteCampaign();
  const globalPanicM = useGlobalPanic();
  // rootsOnly: false — el árbol completo (4 niveles) hace falta para que `collapseOuIds`/`ouLabels`
  // resuelvan nombres de OUs profundas en la columna Alcance (con solo las 6 raíces, cualquier id que
  // no fuera raíz caía al fallback `?? id` y mostraba el GUID crudo) y para que `idsEnAlcance`
  // expanda correctamente el scope de un admin cuya cascada delegada empieza bajo una raíz.
  // activeOnly: false — varias ramas país (LATAM-ECUADOR, LATAM-PERU, etc.) están inactivas pero
  // tienen hijos BU activos; sin esto, `buildOuTree` pierde el padre de esos hijos y los pinta como
  // raíces sueltas duplicadas en vez de anidados (mismo fix que `AdminRoles.tsx`/`OUsSubTab.tsx`).
  const { data: ouItems = [] } = useOUItems({ activeOnly: false, rootsOnly: false });

  // Alcance del admin, para saber qué campañas de la lista puede tocar de verdad. Se calcula ANTES
  // de los early returns de abajo (`mode.kind`) para no volver el hook condicional, igual que el
  // paginador. `recognitionOuScope` (Brillamos), no `ouScope` (Pulso) — ver bug UAT #114.
  const currentUser = useCurrentUser();
  const ouScopeAdmin = currentUser?.profileResolved ? currentUser.recognitionOuScope : undefined;
  const idsAlcanzables = useMemo(() => idsEnAlcance(ouScopeAdmin, ouItems), [ouScopeAdmin, ouItems]);

  // Bug #74 — el listado solo conoce `Campaign.Estado` (Borrador/Activa/Finalizada); la solicitud de
  // aprobación vive en otra colección y nunca se une al DTO. `misSolicitudes` cubre a quien la envió
  // (cualquier rol); `todasPendientes` solo se pide si el rol puede resolverlas (mismo gate que el
  // botón "Aprobaciones" de abajo) — evita un 403 innecesario para el resto de roles.
  //
  // RecognitionCapability.AprobarRechazarCampanas (CapabilityPolicy.cs) exige AdminGlobal, o
  // AdminBrillamos (Admin Reconocimiento) CON el permiso `manageCampaigns` activo — ampliada a Admin
  // Reconocimiento el 2026-08-03, pero ese permiso es ajustable individualmente por administrador
  // (RN-R02), así que el rol solo no basta: hay que consultarlo igual que hace el backend.
  const roles = useEffectiveRoles();
  const tieneManageCampaigns = useHasRecognitionPermission('manageCampaigns');
  const puedeAprobar = roles.includes('AdminGlobal')
    || (roles.includes('AdminBrillamos') && tieneManageCampaigns);
  const misSolicitudes = useMyApprovals();
  const todasPendientes = usePendingApprovals(puedeAprobar);
  const idsPendientesDeAprobacion = useMemo(() => {
    const solicitudes = [...(misSolicitudes.data ?? []), ...(todasPendientes.data ?? [])];
    return new Set(solicitudes.filter((s) => s.estado === 'pendiente').map((s) => s.campaignId));
  }, [misSolicitudes.data, todasPendientes.data]);
  // Tarea 7 — un Admin OU (rol acotado a una rama del árbol de OUs) no gestiona campañas GLOBALES: las
  // 3 tabs y el wizard de alta las rechazaría el backend con 403 en cuanto tocara Guardar
  // (`CampaignsController.RequireScope`). `hasOuScopedAdminRole` es de solo-roles (no específica de
  // Pulso pese a su doc-comment) — se usa tal cual, sin tocarla.
  //
  // 🔴 Bug UAT #114, misma clase aplicada a la navegación: `hasOuScopedAdminRole` SOLA no basta. Un
  // Admin Reconocimiento (AdminBrillamos, sin acotar) que ADEMÁS conserva un rol acotado de Pulso
  // (AdminOU — la pantalla de Administradores de Reconocimiento lo permite a propósito, ver doc-comment
  // de `hasUnscopedRecognitionRole`) tiene `hasOuScopedAdminRole(...) === true` porque 'AdminOU' está en
  // la lista, aunque para Reconocimiento no tenga ninguna restricción real: `useCurrentUser.ts` ya lo
  // corrige para `recognitionOuScope` (fuerza sin-restricción vía `isUnscopedRecognitionAdmin`), pero
  // ese cómputo no llega hasta aquí — `roles` sigue trayendo el rol acotado crudo. Sin este segundo
  // predicado, el gate le escondería las 3 tabs y el wizard a un Admin Reconocimiento real.
  const isOuScoped = hasOuScopedAdminRole(currentUser?.roles ?? [])
    && !hasUnscopedRecognitionRole(currentUser?.roles ?? []);

  // Bug UAT #114 — lo único que hoy cambia para un rol OU-acotado: no se le ofrece «Personalizadas».
  // Antes este mismo predicado le quitaba la pantalla completa (ver la nota del bloque retirado más
  // abajo). Sigue viendo la lista, el wizard —con el árbol de OUs acotado a su alcance— y el panel de
  // excepciones, exactamente como un Admin Global.
  const tabsVisibles: ReadonlyArray<{ id: CampType; emoji: string; labelKey: string }> =
    isOuScoped ? OU_SCOPE_PROGRAM_TABS : CAMP_TABS;

  // Cada tab muestra las campañas de su tipo (Brillamos / Personalizada). Fundamentales no tiene lista.
  // El paginador (maqueta: toda tabla lleva pie) se calcula ANTES de los early returns para no volver el
  // hook condicional (rules-of-hooks).
  const wantType: CampaignType = campType === 'personalizadas' ? 'Personalizada' : 'Brillamos';
  const list = (campaigns.data?.items ?? []).filter((c) => c.tipo === wantType);
  const pager = usePagination(list, 25);

  // Deep-links del aviso de APROBACIÓN de campañas (NotificationService). Se leen una vez y se limpian
  // (replace) para no reabrir el panel al navegar. El hook va aquí, antes de los early returns, para no
  // volverlo condicional (rules-of-hooks).
  //   · ?aprobacion={id} → cola de aprobación, expandiendo esa campaña (validación de que sigue
  //     pendiente la hace el propio ApprovalsPanel: si ya no está en la cola, solo no expande).
  //   · ?excepciones=1  → tab Fundamentales, donde vive el panel de Campañas por Localidad y SU cola.
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const aprobacion = searchParams.get('aprobacion');
    const excepciones = searchParams.get('excepciones');
    if (!aprobacion && !excepciones) return;
    if (aprobacion) setMode({ kind: 'approvals', focusCampaignId: aprobacion });
    else if (excepciones) setCampType('fundamentales');
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('aprobacion');
        next.delete('excepciones');
        return next;
      },
      { replace: true },
    );
  }, [searchParams, setSearchParams]);

  // Tarea 7 — punto de entrada ÚNICO del Admin OU: va ANTES de los modos wizard/aprobaciones para que
  // ningún deep-link (`?aprobacion=`/`?excepciones=`, ver efecto de arriba) pueda abrirle una pantalla
  // que este rol no debe ver. Sin las 3 tabs ni el botón de alta global — solo el panel de excepciones
  // ("Campañas por OU", Tarea 8) tras un selector de 2 opciones (Fundamentales/Brillamos), porque la
  // excepción es siempre a nivel PROGRAMA. Cuando `isOuScoped` es `false` el resto de esta función
  // sigue exactamente igual.
  /*
   * ══════════════════════════════════════════════════════════════════════════════════════════════
   * 🔴 ADRIANA: ESTO REVIERTE EL CORTE DE TU TAREA 7 (`a9a9830`, 17-ago-2026). Lee esto antes de
   *    volver a ponerlo — se retiró a propósito y con una razón concreta, no por descuido.
   * ══════════════════════════════════════════════════════════════════════════════════════════════
   *
   * QUÉ HABÍA AQUÍ: un `if (isOuScoped) return …` que, para un rol OU-acotado, REEMPLAZABA la pantalla
   * entera de Campañas por el panel de excepciones. Ni tabs, ni lista, ni wizard.
   *
   * POR QUÉ SE RETIRA — dos razones que apuntan al mismo sitio:
   *
   *   1. Deja sin cumplir el propio #114. La fila dice, literal: «Los roles de Administrador OU y
   *      Administrador Reconocimiento NO PERMITEN LA CREACIÓN DE CAMPAÑAS». El fix del 13-ago
   *      (`0d8f691`) lo resolvió; Tarea 7, cuatro días después, volvió a dejar al Admin OU sin poder
   *      crear una campaña. El retest del 19-ago falló con ese rol.
   *   2. Contradice tu propia indicación. Sobre las «Campañas por OU» dijiste: *«si quieren hacer
   *      esto, deben hacer una campaña desde cero para la OU»*. Correcto — y con este corte puesto era
   *      IMPOSIBLE de ejecutar precisamente con el rol al que va dirigida: el Admin OU solo alcanzaba
   *      el panel de excepciones, que sobreescribe una campaña activa pero no puede crear ninguna.
   *
   * POR QUÉ ES SEGURO — el problema que Tarea 7 quería evitar ya no existe por otra vía:
   *
   *   · Tu preocupación era mostrar tabs y un wizard que el backend rechazaría con 403 al guardar
   *     (`CampaignsController.RequireScope`). Ese 403 se resolvió cuatro días ANTES de Tarea 7, en el
   *     fix del #114: el árbol de OUs del wizard se acota al alcance delegado del admin (`scopedItems`
   *     vía `recognitionOuScope`), así que ya no puede seleccionar OUs ajenas y no llega a producir
   *     ese rechazo. La defensa del backend sigue en su sitio como red, no como el único control.
   *   · Además, «Editar» y «Duplicar» se deshabilitan con el motivo a la vista cuando la campaña sale
   *     de su alcance (`campanaFueraDeAlcance`) — la 2ª entrega del #114, que Tarea 7 dio por
   *     innecesaria al ocultar la lista. Vuelve a estar en uso.
   *   · Y el alta exige alcance no vacío + fechas + nombre en los 7 idiomas antes de habilitar
   *     Guardar (`publishErrors`), así que no hay forma de mandar un alta que el servidor vaya a
   *     rechazar por incompleta.
   *
   * QUÉ SE CONSERVA DE TAREA 7: al rol acotado NO se le ofrecen las tres tabs, solo las dos de
   * programa (sin «Personalizadas») — ver `tabsVisibles`. Se recupera la capacidad, no el ruido.
   *
   * CONSECUENCIA A TENER PRESENTE: Tarea 7 retiró dos pruebas de `campaignRowActions.test.tsx` (el
   * deshabilitado de Editar/Duplicar fuera de alcance para un Admin OU) razonando que «ese escenario
   * ya no puede ocurrir, el rol nunca vuelve a alcanzar esa lista». Ahora SÍ puede ocurrir: conviene
   * restaurarlas.
   */

  if (mode.kind === 'wizard') {
    return <CampaignWizard key={mode.draft.id ?? 'new'} draft={mode.draft} onDone={() => setMode({ kind: 'list' })} />;
  }
  if (mode.kind === 'approvals') {
    return <ApprovalsPanel onBack={() => setMode({ kind: 'list' })} focusCampaignId={mode.focusCampaignId} />;
  }

  return (
    <AdminSection title={t('admin.campaigns.title')} description={t('admin.campaigns.descLong')}>
      <div className="adm-camp-tabs" role="tablist" aria-label={t('admin.campaigns.title')}>
        {tabsVisibles.map((tab) => (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={campType === tab.id}
            className={`adm-camp-tab ${campType === tab.id ? 'adm-camp-tab--on' : ''}`}
            onClick={() => setCampType(tab.id)}
          >
            <span aria-hidden="true">{tab.emoji}</span> {t(tab.labelKey)}
          </button>
        ))}
      </div>

      {campType === 'fundamentales' && <FundamentalesPanel />}

      {/* Banner PROPIO de Brillamos (identidad del programa, editable) — arriba de la lista de campañas.
          Solo Admin Global; el gate va aquí para que el componente (y su GET de Gestión) ni se monte
          para un admin no-global que no puede editarlo. */}
      {campType === 'brillamos' && (
        <RoleGuard allow={['AdminGlobal']} fallback={null}><BrillamosBannerEditor /></RoleGuard>
      )}

      {/* Config INLINE de la campaña Brillamos recurrente (1:1 maqueta): Status + Fecha/Zona/Aplican puntos
          + tabla "Horarios y Fechas por País". Solo si hay una campaña Brillamos que editar. Va ARRIBA de
          «Modalidad de reconocimiento (entry points)» (QA, 19-oct-2026), justo después del banner. */}
      {campType === 'brillamos' && (
        // Informativa: la ven el Admin OU de Reconocimiento (necesita los lapsos de TODAS las OUs para saber
        // cuándo se puede reconocer cross-OU), el Admin de Reconocimiento y el Admin General.
        <RoleGuard allow={['AdminGlobal', 'AdminBrillamos', 'AdminOUBrillamos']} fallback={null}><BrillamosConfigInline /></RoleGuard>
      )}

      {/* WP — Reglas de envío PROPIAS de Brillamos (modelo por-programa). Detrás de un feature flag de
          DEV (`programRulesDevEnabled`): OFF por defecto para no afectar las pruebas con usuarios; un dev
          lo prende con `localStorage.setItem('ff_program_rules','1')`. El backend además ignora los
          overrides por programa salvo que su propio flag esté ON, así que esto no toca las pruebas. */}
      {campType === 'brillamos' && programRulesDevEnabled() && (
        <RoleGuard allow={['AdminGlobal']} fallback={null}><BrillamosRulesPanel /></RoleGuard>
      )}

      {campType !== 'fundamentales' && (<>
      <h4 className="adm-sub-title">{t('admin.campaigns.statusDivider')}</h4>
      <div className="adm-toolbar">
        <div className="adm-chip-row">
          {campType === 'brillamos'
            ? BRILLAMOS_FILTERS.map((s) => (
              <button key={s} type="button" className={`adm-scope-chip ${filtroBrillamos === s ? 'adm-scope-chip--on' : ''}`} onClick={() => setFiltroBrillamos(s)}>
                {s === 'all' ? t('admin.campaigns.filterAll') : t(`admin.campaigns.status.${s}`)}
              </button>
            ))
            : STATUS_FILTERS.map((s) => (
              <button key={s} type="button" className={`adm-scope-chip ${status === s ? 'adm-scope-chip--on' : ''}`} onClick={() => setStatus(s)}>
                {s === 'all' ? t('admin.campaigns.filterAll') : t(`admin.campaigns.status.${s}`)}
              </button>
            ))}
        </div>
        <span className="adm-spacer" />
        {campType === 'brillamos' && (
          <RoleGuard allow={['AdminGlobal']} fallback={null}>
            {/* RF-073 — pánico GLOBAL Brillamos: finaliza TODAS las campañas activas. Deshabilitado
                si no hay ninguna activa visible (RN-C08, exclusivo del Admin General). */}
            <button
              type="button"
              className="btn btn--danger"
              disabled={globalPanicM.isPending || !list.some((c) => c.estado === 'Activa')}
              onClick={() => setPanicConfirm({ kind: 'global' })}
            >
              <i className="fas fa-triangle-exclamation" /> {t('admin.campaigns.globalPanic')}
            </button>
          </RoleGuard>
        )}
        {puedeAprobar && (
          <button type="button" className="btn btn--secondary" onClick={() => setMode({ kind: 'approvals' })}>
            <i className="fas fa-clipboard-check" /> {t('admin.campaigns.approvals')}
          </button>
        )}
        <button type="button" className="btn btn--primary" onClick={() => setMode({ kind: 'wizard', draft: newDraft(wantType) })}>
          <i className="fas fa-plus" /> {t('admin.campaigns.new')}
        </button>
      </div>

      <StateBlock
        isLoading={campaigns.isLoading}
        isError={campaigns.isError}
        error={campaigns.error}
        isEmpty={list.length === 0}
        emptyText={t('admin.campaigns.empty')}
        onRetry={() => { campaigns.refetch(); }}
      >
        <div className="adm-table-wrap">
          <table className="adm-table">
            <thead>
              <tr>
                <th>{t('admin.campaigns.colName')}</th>
                <th>{t('admin.campaigns.colType')}</th>
                <th>{t('admin.campaigns.colStatus')}</th>
                <th>{t('admin.campaigns.colScope')}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {pager.slice.map((c) => {
                // El admin de OU ve TODAS las campañas del tenant (la lista no filtra por alcance),
                // pero solo puede guardar las que caen enteras dentro del suyo.
                const fueraDeAlcance = campanaFueraDeAlcance(c.alcance, idsAlcanzables);
                return (
                <tr key={c.id}>
                  <td>{getCampaignName(c, lang)}</td>
                  <td>{t(`admin.campaigns.type.${c.tipo}`)}</td>
                  {/* Solo aplica en Borrador — Activa/Finalizada no dejan un "pendiente" real vigente
                      (aprobar activa la campaña Y resuelve la solicitud en el mismo paso), así que
                      condicionar aquí evita fiarse de que esa invariante nunca se rompa río arriba. */}
                  {(() => {
                    // ADR 0014 — en Brillamos la etiqueta sale TAL CUAL del estado visible que calcula
                    // el servidor: ni vigencia en el cliente ni «Activa · fuera de ventana».
                    if (c.tipo === 'Brillamos') {
                      const visible = estadoVisibleBrillamos(c);
                      return (
                        <td>
                          <span className={badgeEstadoVisible(visible)}>
                            {t(`admin.campaigns.status.${visible}`)}
                          </span>
                        </td>
                      );
                    }
                    const pendienteAprobacion = c.estado === 'Borrador' && idsPendientesDeAprobacion.has(c.id);
                    // Bug UAT #114 — el matiz de vigencia, para que "Activa" no signifique dos cosas
                    // distintas. El `title` lleva el porqué: el badge solo tiene sitio para el qué.
                    const vigencia = vigenciaDeCampana(c);
                    const desalineada = c.estado === 'Activa' && vigencia !== 'vigente';
                    return (
                      <td>
                        <span
                          className={statusBadge(c.estado, pendienteAprobacion, vigencia)}
                          title={desalineada && !pendienteAprobacion
                            ? t(`admin.campaigns.status.${vigencia === 'vencida' ? 'ActivaVencidaHint' : 'ActivaPorIniciarHint'}`)
                            : undefined}
                        >
                          {statusLabel(t, c.estado, pendienteAprobacion, vigencia)}
                        </span>
                      </td>
                    );
                  })()}
                  <td>{c.alcance.length ? ouLabels(collapseOuIds(c.alcance, ouItems), ouItems) : t('admin.campaigns.allScopes')}</td>
                  <td>
                    <div className="adm-actions">
                      {/*
                        * 🔴 «Editar» NO se ofrece en una campaña Finalizada. El botón se pintaba
                        * siempre y `Campaign.UpdateDraft` lanza «No se puede editar una campaña
                        * finalizada»: abría el wizard, el admin rellenaba y se llevaba un 422.
                        * Salió al preguntarse qué se ve al entrar a una finalizada desde el reporte.
                        */}
                      {/*
                        * Y tampoco se ofrece si la campaña sale de su alcance: el guardado
                        * terminaría en 403 tras rellenar el wizard entero (ver
                        * `campanaFueraDeAlcance`). Se deshabilita con el motivo a la vista en vez de
                        * ocultarla: la campaña SÍ aparece en la lista, así que esconder la acción sin
                        * explicación se lee como un fallo.
                        */}
                      {puedeEditarCampana(c) && (
                        <button type="button" className="btn btn--link" disabled={fueraDeAlcance}
                          title={fueraDeAlcance ? t('admin.campaigns.outOfScopeHint') : undefined}
                          onClick={() => setMode({ kind: 'wizard', draft: toDraft(c) })}>
                          <i className="fas fa-pen" /> {t('admin.campaigns.editBtn')}
                        </button>
                      )}
                      {/* Duplicar copia el alcance de la original (el backend no lo acota si no se
                          manda uno nuevo): duplicar una campaña ajena crearía otra igual de
                          inalcanzable, imposible de editar después. Mismo criterio. */}
                      <button type="button" className="btn btn--link" disabled={duplicateM.isPending || fueraDeAlcance}
                        title={fueraDeAlcance ? t('admin.campaigns.outOfScopeHint') : undefined}
                        onClick={() => duplicateM.mutate({ id: c.id })}>
                        <i className="fas fa-copy" /> {t('admin.campaigns.duplicate')}
                      </button>
                      {puedeDetenerCampana(c) && (
                        <button type="button" className="btn btn--danger" disabled={panicM.isPending}
                          onClick={() => setPanicConfirm({ kind: 'panic', campaign: c })}>
                          <i className="fas fa-triangle-exclamation" /> {t('admin.campaigns.panic')}
                        </button>
                      )}
                      {/*
                        * C2/P18-bis — deshacer un pánico accidental. Solo se ofrece mientras el
                        * servidor lo va a aceptar (`puedeDeshacerPanico`): cerrada POR PÁNICO, hace
                        * menos de 24 h y con la fecha de fin sin vencer. Antes solo se miraba lo
                        * primero y el botón seguía días después para que el servidor lo rechazara.
                        * Si aun así dice que no (desfase de reloj), se enseña su motivo tal cual.
                        */}
                      {puedeDeshacerPanico(c) && (
                        <button type="button" className="btn btn--link" disabled={undoPanicM.isPending}
                          onClick={() => setPanicConfirm({ kind: 'undo', campaign: c })}>
                          <i className="fas fa-rotate-left" /> {t('admin.campaigns.undoPanic')}
                        </button>
                      )}
                      {puedeBorrarCampana(c, fueraDeAlcance) && (
                        <button type="button" className="btn btn--link" disabled={deleteM.isPending}
                          onClick={() => setDeleteConfirm(c)}>
                          <i className="fas fa-trash" /> {t('admin.campaigns.deleteBtn')}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {list.length > 0 && (
          <AdminPagerFooter
            total={list.length}
            page={pager.page}
            pageSize={pager.pageSize}
            onPage={pager.setPage}
            onPageSize={(s) => pager.setPageSize(s === 0 ? list.length : s)}
          />
        )}
        {(duplicateM.isError || panicM.isError || globalPanicM.isError || undoPanicM.isError || deleteM.isError) && <p className="adm-error" role="alert"><i className="fas fa-triangle-exclamation" /> {getRecognitionErrorMessage(duplicateM.error || panicM.error || globalPanicM.error || undoPanicM.error || deleteM.error, t('admin.saveError'))}</p>}
        {deleteM.isSuccess && <p className="adm-success" role="status">{t('admin.campaigns.deleteDone')}</p>}
        {globalPanicM.isSuccess && <p className="adm-success">{t('admin.campaigns.globalPanicDone', { count: globalPanicM.data?.finalizadas ?? 0 })}</p>}
      </StateBlock>
      </>)}

      {/* Ticket 05 (estados de campaña Brillamos, ADR 0004) — la pestaña Brillamos YA NO monta el panel de
          excepciones por OU (tabla, cola y asistente): en Brillamos solo rige la campaña y la API rechaza
          crear o hacer avanzar excepciones de ese programa. Fundamentales conserva el suyo
          (`FundamentalesPanel`), y el enlace `?excepciones=1` sigue llevando allí. */}

      {panicConfirm?.kind === 'global' && (
        <ConfirmModal
          title={t('admin.campaigns.globalPanic')}
          message={t('admin.campaigns.globalPanicConfirm')}
          confirmText={t('admin.campaigns.globalPanic')}
          onConfirm={() => globalPanicM.mutate()}
          onClose={() => setPanicConfirm(null)}
        />
      )}
      {panicConfirm?.kind === 'panic' && (
        <ConfirmModal
          title={t('admin.campaigns.panic')}
          message={t('admin.campaigns.panicConfirm')}
          confirmText={t('admin.campaigns.panic')}
          onConfirm={() => panicM.mutate(panicConfirm.campaign.id)}
          onClose={() => setPanicConfirm(null)}
        />
      )}
      {panicConfirm?.kind === 'undo' && (
        <ConfirmModal
          type="warning"
          title={t('admin.campaigns.undoPanic')}
          message={t('admin.campaigns.undoPanicConfirm')}
          confirmText={t('admin.campaigns.undoPanic')}
          onConfirm={() => undoPanicM.mutate(panicConfirm.campaign.id)}
          onClose={() => setPanicConfirm(null)}
        />
      )}
      {deleteConfirm && (
        <ConfirmModal
          title={t('admin.campaigns.deleteConfirmTitle')}
          message={t(estadoVisibleBrillamos(deleteConfirm) === 'PendienteAprobacion'
            ? 'admin.campaigns.deleteConfirmPending'
            : 'admin.campaigns.deleteConfirm', { name: getCampaignName(deleteConfirm, lang) })}
          confirmText={t('admin.campaigns.deleteBtn')}
          onConfirm={() => deleteM.mutate(deleteConfirm.id)}
          onClose={() => setDeleteConfirm(null)}
        />
      )}
    </AdminSection>
  );
}

// ─── Wizard de 5 pasos ────────────────────────────────────────────────────────
const STEPS = ['info', 'design', 'audience', 'config', 'documents', 'publish'] as const;
export type CampaignWizardStep = typeof STEPS[number];
type Step = CampaignWizardStep;

/** Bug UAT #16 (Sesión 1 México, 10/08/2026) — ¿se muestra «Guardar borrador» en este paso?
 *
 *  Al CREAR, no: los pasos de captura solo llevan Atrás/Siguiente. Un Guardar al lado de Siguiente
 *  desde el paso 1 se lee como si hubiera dos maneras de avanzar, y encima no podía guardar nada
 *  (el alta exige la configuración completa — bug #111). El botón entra en Documentos, que es el
 *  primer paso que NECESITA el borrador guardado (`saveDraftFirst`) y al que solo se llega con la
 *  configuración ya válida.
 *
 *  Al EDITAR una campaña que ya existe, sí, en todos los pasos: ahí cada paso es una edición
 *  independiente y esconderlo obligaría a recorrer el wizard entero para guardar un cambio de
 *  nombre. */
export function guardarVisibleEnPaso(step: CampaignWizardStep, esAltaNueva: boolean): boolean {
  return !esAltaNueva || STEPS.indexOf(step) >= STEPS.indexOf('documents');
}
// Paridad visual con la maqueta (§26.9): cada paso lleva su ícono FontAwesome en el stepper vertical.
const STEP_ICONS: Record<Step, string> = {
  info: 'fa-file-alt', design: 'fa-palette', audience: 'fa-globe',
  config: 'fa-sliders-h', documents: 'fa-file-pdf', publish: 'fa-paper-plane',
};

function CampaignWizard({ draft: initial, onDone }: Readonly<{ draft: CampaignDraft; onDone: () => void }>) {
  const { t, i18n } = useTranslation('recognition');
  const [draft, setDraft] = useState<CampaignDraft>(initial);
  // Fechas con las que se abrió el wizard: lo ya guardado no se bloquea por ser pasado (campañas
  // iniciadas); solo se impide ELEGIR una fecha pasada nueva. Ver `campaignPastDates`.
  const [original] = useState(() => ({
    fechaInicio: initial.fechaInicio, fechaFin: initial.fechaFin,
  }));
  const hoy = todayInTimeZone(draft.zonaHoraria);
  const [step, setStep] = useState<Step>('info');
  const [dirty, setDirty] = useState(false);

  // Guarda de cambios sin guardar: mientras el wizard tenga ediciones sin guardar, una navegación
  // que lo desmonte (p. ej. clic en una notificación de la campana) pide confirmación antes.
  useUnsavedChangesGuard(dirty);

  const createM = useCreateCampaign();
  const updateM = useUpdateCampaign();
  const approvalM = useCampaignApproval();
  // rootsOnly: false — el wizard debe poder ofrecer OUs de nivel Departamento/Site, no solo raíz.
  // activeOnly: false — ver comentario equivalente en `BrillamosConfigInline` más arriba: sin esto,
  // las ramas país inactivas rompen la cadena de padres y el árbol muestra raíces duplicadas.
  const { data: ouItems = [] } = useOUItems({ activeOnly: false, rootsOnly: false });
  const currentUser = useCurrentUser();

  // WP-15 (§26/RF-081) — gating por rol: un admin no-global solo ve/edita SU cascada de OUs (se oculta
  // "Global" y las ramas ajenas). El backend además rechaza alcance fuera de scope (defensa real); esto
  // es la capa de UX. Global (allOusAccess) ve el árbol completo.
  // Solo se aplica cuando el perfil YA resolvió (profileResolved): mientras carga, el scope es el
  // fallback vacío y filtrar dejaría el árbol vacío para TODOS (flicker) — hallazgo de la revisión.
  // `recognitionOuScope` (Brillamos), NO `ouScope` (Pulso): un AdminOUBrillamos sin rol de Pulso
  // tenía `ouScope` siempre vacío y veía el árbol de alcance de campañas en blanco (fix 2026-08-12).
  const ouScope = currentUser?.profileResolved ? currentUser.recognitionOuScope : undefined;
  const scopeIds = useMemo(() => idsEnAlcance(ouScope, ouItems), [ouScope, ouItems]);
  // Si la campaña ya incluye OUs FUERA del scope (creada por un global), se muestra el árbol COMPLETO:
  // filtrarlas las esconde y provoca des-marcados fantasma (el padre invisible se re-expande al guardar,
  // hallazgo de la revisión). Cuando el alcance completo está en-scope, se filtra a la cascada propia.
  // (A esta rama ya casi no se llega: la lista deshabilita «Editar» en esas campañas — el guardado
  // acababa en 403. Se conserva porque el alcance puede quedar fuera por otras vías, p. ej. una OU
  // creada bajo su rama DESPUÉS de que se le asignara el alcance.)
  const hasOutOfScope = campanaFueraDeAlcance([...draft.alcance, ...draft.exclusiones], scopeIds);
  const scopedItems = useMemo(
    () => (scopeIds && !hasOutOfScope ? ouItems.filter((o) => scopeIds.has(o.id)) : ouItems),
    [scopeIds, hasOutOfScope, ouItems],
  );
  // Bug UAT #22 (red de seguridad) — un admin ACOTADO cuyo alcance de Brillamos no resuelve a
  // ninguna OU del catálogo (sin OUs asignadas, o asignadas a una OU inactiva/inexistente): el árbol
  // sale vacío. Sin esto, el paso de Audiencia se ve en blanco y bloquea en silencio. Se exige
  // `ouItems.length > 0` para no confundir "catálogo aún cargando" con "alcance vacío" (evita parpadeo).
  const scopeEmpty = scopeIds != null && ouItems.length > 0 && scopedItems.length === 0;

  // Preselecciona el alcance completo del admin no-global al crear una campaña nueva: es el default
  // cómodo (la mayoría de campañas apuntan a toda su área). Solo default, NO bloqueo — el admin puede
  // desmarcar libremente para acotar a un sub-alcance dentro de su scope.
  useEffect(() => {
    if (draft.id || !ouScope || ouScope.allOusAccess || ouScope.rootOuIds.length === 0) return;
    setDraft((d) => (d.alcance.length === 0 ? { ...d, alcance: [...ouScope.rootOuIds] } : d));
  }, [ouScope, draft.id]);

  // Bug UAT (13/08/2026, 2ª ronda) — NO se bloquea ninguna OU del árbol de alcance. El admin de OU
  // debe poder elegir CUALQUIER subconjunto dentro de su scope ("diferentes alcances dentro de su
  // scope, solo lo de sus permisos"). El intento previo (RF-081) bloqueaba las raíces del alcance y
  // seguía congelando la selección: `recognitionOuScope.rootOuIds` llega EXPANDIDO desde el backend y,
  // según cómo esté guardado el scope, ni colapsar a las raíces liberaba la selección. El límite real
  // ("solo lo de sus permisos") ya lo imponen DOS capas independientes, sin candado: el árbol filtrado
  // a `scopedItems` (no ve/elige OUs fuera de su scope) y el backend (`CampaignsController.RequireScope`
  // → 403 si el alcance sale del scope). El backend NO exige incluir la OU propia, así que quitar el
  // lock no provoca rechazos.

  // WP-B9 (RF-071) — el pre-chequeo de solape necesita las campañas Activas. `pageSize` > 100 el
  // backend lo DESCARTA y cae a 20 (`GetCampaignsQueryHandler:15`), así que 100 es el techo real;
  // si hay más, se dice —callarlo convertiría «no encontré conflicto» en una afirmación falsa.
  //
  // Y avisa, no garantiza: el backend decide al ACTIVAR y esto se evalúa al enviar a aprobación.
  // Entre una cosa y otra la campaña rival puede finalizar (aviso de más) o nacer una nueva (aviso
  // de menos). Sirve para no comerse el rechazo tres pasos después, no para sustituirlo.
  const activasQuery = useCampaigns('Activa', 1, 100);
  // `?? []` crea un array nuevo en cada render; sin memo, los `useMemo` de abajo no memorizan nada.
  const activas = useMemo(() => activasQuery.data?.items ?? [], [activasQuery.data]);
  const activasIncompletas = (activasQuery.data?.totalCount ?? 0) > activas.length;
  const alcanceExpandido = useMemo(() => expandOuIds(draft.alcance, ouItems), [draft.alcance, ouItems]);
  const conflictos = useMemo(
    () => campanasEnConflicto(alcanceExpandido, activas, draft.id),
    [alcanceExpandido, activas, draft.id],
  );
  const locked = draft.estado === 'Activa' || draft.estado === 'Finalizada';
  const patch = (p: Partial<CampaignDraft>) => {
    setDraft((d) => ({ ...d, ...p }));
    setDirty(true);
    // Bug UAT #111 — el error de un guardado fallido se quedaba pintado indefinidamente: react-query
    // conserva el error de la mutación hasta el siguiente intento, así que el admin completaba toda la
    // configuración y seguía viendo el mismo mensaje rojo, como si nada de lo que hiciera lo arreglara.
    // Editar cualquier campo lo borra: describe un intento anterior, sobre datos que ya cambiaron.
    if (createM.isError) createM.reset();
    if (updateM.isError) updateM.reset();
  };
  const patchVis = (p: Partial<CampaignVisibilityDto>) => patch({ visibilidad: { ...draft.visibilidad, ...p } });
  // Multi-persona / multi-fundamental por campaña (2026-09-29): lo que la campaña hereda mientras no
  // tenga valor propio (pestaña Brillamos ?? Gestión). Se pide siempre: los hooks no pueden ser
  // condicionales, y solo se usa con `tipo === 'Brillamos'`.
  const brillamosProgramQ = useProgramSettings('Brillamos');
  const gestionQ = useGestionSettings();
  const multiEnvio = multiEnvioEfectivo(draft.visibilidad, brillamosProgramQ.data, gestionQ.data);
  // ADR 0005 (2026-10-15): dentro de la campaña Europa ya no tiene piso de 1 y 1. Si el alcance toca OUs
  // GDPR y se permite más, se avisa (sin confirmar ni bloquear): la restricción es de quien configura.
  const avisoEuropa = (multiEnvio.multipersona || multiEnvio.multiFundamental)
    && alcanceIncluyeGdpr(draft.alcance, ouItems);
  // Modalidad de entrada por campaña (2026-09-29): misma herencia y mismo guardado explícito.
  const modalidad = modalidadEntradaEfectiva(draft.visibilidad, brillamosProgramQ.data, gestionQ.data);
  // Se guardan explícitos solo cuando se conoce lo heredado: con un default inventado, la campaña
  // cambiaría de comportamiento al guardarse. Sin herencia, viajan `null` y el servidor hereda igual.
  const visibilidadAGuardar: CampaignVisibilityDto =
    draft.tipo === 'Brillamos' && brillamosProgramQ.isSuccess && gestionQ.isSuccess
      ? { ...draft.visibilidad, ...multiEnvio, modalidadEntrada: modalidad }
      : draft.visibilidad;
  // WP-02 — edita el diccionario de diseño (clave→valor). Vacío elimina la clave para no acumular basura.
  const patchDiseno = (key: string, value: string) => {
    const next = { ...draft.diseno };
    if (value === '') delete next[key]; else next[key] = value;
    patch({ diseno: next });
  };
  const disenoBool = (key: string) => draft.diseno[key] === 'true';

  // Traducir Nombre + Descripción al resto de idiomas desde el primero capturado (reusa el Azure
  // Translator de SurveyService, igual que el editor legal). Ayuda a cumplir el requisito de 7 idiomas.
  const [translating, setTranslating] = useState(false);
  const [translateError, setTranslateError] = useState<string | null>(null);
  const infoSourceLang = SUPPORTED_LANGUAGE_CODES.find((code) => draft.nombre[code]?.trim());
  const handleTranslateInfo = async () => {
    if (!infoSourceLang || translating) return;
    setTranslateError(null);
    const targetLangs = SUPPORTED_LANGUAGE_CODES.filter((code) => code !== infoSourceLang);
    const texts = [draft.nombre[infoSourceLang] ?? '', draft.descripcion[infoSourceLang] ?? ''];
    setTranslating(true);
    try {
      const response = await translationApi.translateTexts({ sourceLang: infoSourceLang, targetLangs, texts });
      const nombre = { ...draft.nombre };
      const descripcion = { ...draft.descripcion };
      for (const lang of targetLangs) {
        nombre[lang] = response.translations[lang]?.[0] ?? nombre[lang] ?? '';
        if (texts[1]) descripcion[lang] = response.translations[lang]?.[1] ?? descripcion[lang] ?? '';
      }
      patch({ nombre, descripcion });
    } catch (error) {
      setTranslateError(
        error instanceof SessionExpiredError
          ? t('admin.translate.sessionExpired')
          : getRecognitionErrorMessage(error, t('admin.docs.translateError', 'No se pudo traducir automáticamente.')),
      );
    } finally {
      setTranslating(false);
    }
  };

  const stepIndex = STEPS.indexOf(step);
  const isLast = stepIndex === STEPS.length - 1;

  // WP-03 (§26) — validación inline por paso + bloqueo de navegación hacia adelante. Cada paso reporta
  // sus faltantes; avanzar (botón o stepper) se detiene en el primer paso inválido y marca sus errores.
  // Retroceder siempre se permite. Alineado a los requisitos de publicación (7 idiomas + alcance + fechas).
  const [touched, setTouched] = useState<Partial<Record<Step, boolean>>>({});
  const stepErrors = (s: Step): string[] => {
    const errs: string[] = [];
    if (s === 'info' && !hasAllLanguages(draft.nombre)) errs.push(t('admin.campaigns.valNameAllLangs'));
    if (s === 'audience' && draft.alcance.length === 0) errs.push(t('admin.campaigns.valScope'));
    if (s === 'audience') {
      // RF-071 — solo bloquea si la campaña AÚN no está Activa. Con la campaña ya activa el backend
      // no revalida el alcance entero, solo las OUs AÑADIDAS (`UpdateCampaignCommandHandler:55`);
      // bloquear ahí sería inventar un rechazo que el backend no hace, y dejaría la campaña sin
      // poder editarse por un solape que ya existía antes de tocarla.
      if (draft.estado !== 'Activa' && conflictos.length > 0) {
        errs.push(t('admin.campaigns.valScopeConflict', {
          campanas: conflictos.map((c) => pickLocalized(c.nombre, i18n.language)).join(', '),
        }));
      }
      // Ya NO se valida "no excluir el nodo propio": el admin de OU puede acotar y excluir sub-ramas
      // libremente dentro de su scope (bug UAT 2ª ronda). El límite lo imponen `scopedItems` + el
      // backend (`RequireScope`), no el nodo propio.
    }
    if (s === 'config') {
      if (!draft.fechaInicio) errs.push(t('admin.campaigns.valStart'));
      if (!draft.fechaFin) errs.push(t('admin.campaigns.valEnd'));
      // Bug UAT #113 — `<=`, no `<`. El backend exige la fecha de fin ESTRICTAMENTE posterior
      // (`CreateCampaignCommandValidator`: `x.FechaFin > x.FechaInicio`), así que una campaña de un
      // solo día pasaba este filtro, encendía "Guardar" y moría en el alta con un 400 que en pantalla
      // solo decía "Error de validación en los datos enviados". El texto del rótulo decía "igual o
      // posterior" y prometía justo lo que el servidor no acepta: se corrigió en los 7 idiomas.
      if (draft.fechaInicio && draft.fechaFin && draft.fechaFin <= draft.fechaInicio) errs.push(t('admin.campaigns.valDateOrder'));
      // QA 16-oct-2026 — no se admiten fechas anteriores a hoy (zona de la campaña) salvo las que la
      // campaña ya traía guardadas. El selector las bloquea con `min`; esto cubre teclearlas a mano.
      if (esFechaPasadaNoPermitida(draft.fechaInicio, hoy, original.fechaInicio)
        || esFechaPasadaNoPermitida(draft.fechaFin, hoy, original.fechaFin)) {
        errs.push(t('admin.campaigns.valPastDate'));
      }
    }
    return errs;
  };
  const currentErrors = stepErrors(step);
  // Defensa en profundidad (hallazgo de la revisión): enviar a aprobación no debe depender SOLO de
  // haber pasado la navegación; se valida el conjunto completo aquí también. Guardar Borrador sí puede
  // quedar incompleto — el gate es para la publicación/envío, no para el borrador.
  const publishErrors = STEPS.flatMap(stepErrors);
  // Bug UAT #111 (Sesión 2 Europa, 12/08/2026) — "Guardar" se habilitaba en cuanto había nombre y
  // alcance, es decir desde el paso Audiencia; pero el alta EXIGE fechas de inicio y fin, así que el
  // clic acababa en un 400 y la campaña no se creaba. Ahora el botón exige la configuración COMPLETA
  // (los mismos requisitos que valida el wizard paso a paso), por lo que en la práctica se enciende
  // al terminar Configuración —ya en Documentos— y nunca ofrece un guardado que el backend rechaza.
  // Se prefiere deshabilitarlo (con el motivo a la vista) antes que ocultarlo: un botón que aparece
  // de la nada se lee como "no puedo guardar" y no dice qué falta.
  const canSave = publishErrors.length === 0;
  // Bug UAT #16 — el pie del wizard no ofrece Guardar mientras se captura un alta nueva (ver
  // `guardarVisibleEnPaso`). Se conserva el criterio del #111 para cuando SÍ se muestra: si está
  // apagado, el motivo queda a la vista (`publishRequirements`) — nunca un botón mudo.
  const showSave = guardarVisibleEnPaso(step, !draft.id);
  const goTo = (target: Step) => {
    const targetIndex = STEPS.indexOf(target);
    if (targetIndex <= stepIndex) { setStep(target); return; } // retroceder o quedarse: libre
    for (let i = stepIndex; i < targetIndex; i++) {
      const s = STEPS[i];
      if (stepErrors(s).length > 0) { setTouched((tch) => ({ ...tch, [s]: true })); setStep(s); return; }
    }
    setStep(target);
  };

  const streakEdit = parseStreakMinimum(draft.streakWeeklyMinimum);
  const streakMinimum = typeof streakEdit === 'number' ? streakEdit : null;
  const maxRecognitionsEdit = parseMaxRecognitionsPerPeriod(draft.maxRecognitionsPerPeriod);
  const maxRecognitionsPerPeriod = typeof maxRecognitionsEdit === 'number' ? maxRecognitionsEdit : null;
  // Bug UAT #18 — «veces mismo fundamental por persona»: mismo rango/parseo que el tope de envíos.
  const maxSameFundEdit = parseMaxRecognitionsPerPeriod(draft.maxSameFundamentalPerPerson);
  const maxSameFundamentalPerPerson = typeof maxSameFundEdit === 'number' ? maxSameFundEdit : null;

  const save = (after?: () => void) => {
    // WP-01: se persiste el alcance/exclusiones EXPANDIDO (padre → descendientes) para que el matcher
    // literal del backend alcance a las OUs hijas.
    const alcance = expandOuIds(draft.alcance, ouItems);
    const exclusiones = expandOuIds(draft.exclusiones, ouItems);
    // Banner propio de la campaña — mismo gate REQ-034 que `diseno`: Brillamos no lo admite. Se
    // resuelve una sola vez para los dos cuerpos (alta/edición) de abajo.
    const bannerSubtitle = draft.tipo === 'Brillamos' ? null : draft.bannerSubtitle;
    const bannerLogoUrl = draft.tipo === 'Brillamos' ? null : draft.bannerLogoUrl;
    if (draft.id) {
      const body: UpdateCampaignBody = {
        nombre: draft.nombre, descripcion: draft.descripcion,
        fechaInicio: toUtcDayIso(draft.fechaInicio), fechaFin: toUtcDayEndIso(draft.fechaFin),
        zonaHoraria: draft.zonaHoraria, alcance, exclusiones,
        visibilidad: visibilidadAGuardar,
        // WP-02/REQ-034: la identidad de Brillamos es inmutable → nunca se manda su diseño (el backend
        // lo rechazaría). Para las demás, el diccionario de diseño viaja tal cual.
        diseno: draft.tipo === 'Brillamos' ? null : draft.diseno,
        bannerSubtitle, bannerLogoUrl,
        // A diferencia del mínimo de racha, el tope de envíos SÍ se manda con la campaña Activa: se
        // revisa por envío individual, no al cierre de un período, así que no hay nada que recalificar.
        maxRecognitionsPerPeriod,
        clearMaxRecognitionsPerPeriod: maxRecognitionsEdit === 'clear',
        // Bug UAT #18 — «No aplica» (tercer estado). Se manda siempre el booleano que ve el usuario;
        // con él en true el backend ignora/limpia el número, así que el par de arriba queda coherente.
        recognitionsLimitDisabled: draft.recognitionsLimitDisabled,
        // Bug UAT #18 — «veces mismo fundamental por persona»: mismo patrón (número + clear + No aplica).
        maxSameFundamentalPerPerson,
        clearMaxSameFundamentalPerPerson: maxSameFundEdit === 'clear',
        sameFundamentalLimitDisabled: draft.sameFundamentalLimitDisabled,
        // Cadena vacía la LIMPIA en el backend; `undefined` la dejaría intacta. Aquí siempre
        // se manda lo que el usuario ve, que es lo que el wizard promete al guardar.
        frecuencia: frecuenciaAGuardar(draft.frecuencia),
        // Bug UAT #19 — la meta ya no se captura en pantalla, pero se REENVÍA tal como llegó del DTO
        // para no borrar en silencio una meta fijada por API. Sin `clearMetaParticipantes` (que ya no
        // se manda: ningún control de la UI puede pedir el borrado), `null` significa «no la toques»,
        // así que una campaña sin meta sigue sin meta y una con meta la conserva.
        metaParticipantes: metaParticipantesAGuardar(draft.metaParticipantes),
        // RF-037: vaciar el campo tiene que PODER quitar el mínimo, y en un PATCH `null` significa
        // "sin cambio". Por eso el vacío —y solo el vacío— viaja como la bandera explícita de borrado;
        // una entrada ilegible deja ambos campos sin poner, que es "no lo toques".
        //
        // Con la campaña iniciada NO se manda ninguno de los dos. El backend rechaza el cambio, y
        // mandarlos igual convertiría cualquier guardado del formulario en un 422 en cuanto el valor
        // guardado no coincidiera con lo que el campo muestra —por ejemplo, un mínimo legado fuera de
        // 1–20 que esta pantalla acota al pintarlo—, bloqueando la edición del resto de la campaña.
        ...(locked ? {} : {
          streakWeeklyMinimum: streakMinimum,
          clearStreakWeeklyMinimum: streakEdit === 'clear',
        }),
      };
      updateM.mutate({ id: draft.id, body }, { onSuccess: (c) => { setDraft(toDraft(c)); setDirty(false); after?.(); } });
    } else {
      const body: CreateCampaignBody = {
        nombre: draft.nombre, tipo: draft.tipo, zonaHoraria: draft.zonaHoraria, alcance,
        fechaInicio: toUtcDayIso(draft.fechaInicio), fechaFin: toUtcDayEndIso(draft.fechaFin),
        descripcion: draft.descripcion, exclusiones, visibilidad: visibilidadAGuardar,
        landingSlug: draft.landingSlug || null,
        diseno: draft.tipo === 'Brillamos' ? null : draft.diseno,
        bannerSubtitle, bannerLogoUrl,
        streakWeeklyMinimum: streakMinimum,
        maxRecognitionsPerPeriod,
        recognitionsLimitDisabled: draft.recognitionsLimitDisabled,
        maxSameFundamentalPerPerson,
        sameFundamentalLimitDisabled: draft.sameFundamentalLimitDisabled,
      };
      createM.mutate(body, {
        onSuccess: (c) => { setDraft(toDraft(c)); setDirty(false); after?.(); },
      });
    }
  };

  const saving = createM.isPending || updateM.isPending;
  const saveError = createM.error || updateM.error;
  const [confirmSubmit, setConfirmSubmit] = useState(false);

  return (
    <AdminSection title={draft.id ? t('admin.campaigns.wizardEditTitle') : t('admin.campaigns.wizardNewTitle')} description={t('admin.campaigns.wizardSub')}>
      <div className="adm-toolbar">
        <button type="button" className="btn btn--link" onClick={onDone}><i className="fas fa-arrow-left" /> {t('admin.campaigns.backToList')}</button>
      </div>

      <div className="adm-wizard-layout">
      <div className="adm-wizard-steps">
        {STEPS.map((s, i) => (
          <button key={s} type="button"
            className={`adm-wizard-step ${step === s ? 'adm-wizard-step--on' : ''} ${i < stepIndex ? 'adm-wizard-step--done' : ''}`}
            onClick={() => goTo(s)}>
            <span className="adm-wizard-num">
              {i < stepIndex ? <i className="fas fa-check" /> : <i className={`fas ${STEP_ICONS[s]}`} />}
            </span>
            {t(`admin.campaigns.step.${s}`)}
          </button>
        ))}
      </div>
      <div className="adm-wizard-main">

      <div className="adm-wizard-body">
        {step === 'info' && (
          <>
            <label className="adm-field adm-camp-field--type">
              <span>{t('admin.campaigns.fieldType')}</span>
              <select className="field" value={draft.tipo} disabled={Boolean(draft.id)} onChange={(e) => patch({ tipo: e.target.value as CampaignType })}>
                {/* "Fundamentales" es el default SIEMPRE ACTIVO (RN-C10), no una campaña creable: se
                    excluye de las opciones para no crear una campaña que quedaría fuera de los 3 tabs
                    (WP-00 §26). Se mantiene solo si se está editando una que ya tuviera ese tipo. */}
                {TYPES.filter((ty) => ty !== 'Fundamentales' || draft.tipo === ty).map((ty) => <option key={ty} value={ty}>{t(`admin.campaigns.type.${ty}`)}</option>)}
              </select>
            </label>
            <LocalizedFields label={t('admin.campaigns.fieldName')} value={draft.nombre} onChange={(nombre) => patch({ nombre })} requireAll />
            <LocalizedFields label={t('admin.campaigns.fieldDescription')} value={draft.descripcion} onChange={(descripcion) => patch({ descripcion })} textarea />
            <div className="admin-docs-actions">
              <button
                type="button"
                className="btn btn--secondary"
                disabled={!infoSourceLang || translating}
                onClick={handleTranslateInfo}
                title={infoSourceLang
                  ? t('admin.docs.translateFrom', { lang: infoSourceLang.toUpperCase(), defaultValue: 'Traducir desde {{lang}} a los demás idiomas' })
                  : t('admin.docs.translateHint', 'Captura al menos un idioma para poder traducir')}
              >
                <i className={`fas ${translating ? 'fa-spinner fa-spin' : 'fa-language'}`} />
                {' '}
                {translating ? t('admin.docs.translating', 'Traduciendo…') : t('admin.docs.translate', 'Traducir a los demás idiomas')}
              </button>
            </div>
            {translateError && <p className="adm-error" role="alert"><i className="fas fa-triangle-exclamation" /> {translateError}</p>}
            <label className="adm-field adm-camp-field--slug">
              <span>{t('admin.campaigns.fieldSlug')}</span>
              <input type="text" value={draft.landingSlug} onChange={(e) => patch({ landingSlug: e.target.value })} placeholder="brillamos-2026" />
            </label>
          </>
        )}

        {step === 'design' && (
          <>
            {/* WP-02 (§26, réplica del Step 2 de la maqueta) — Diseño de la campaña. La identidad de
                Brillamos es inmutable (REQ-034): sus controles se deshabilitan con aviso. */}
            {draft.tipo === 'Brillamos' ? (
              <p className="adm-note adm-note--warn"><i className="fas fa-lock" /> {t('admin.campaigns.designBrillamosLocked')}</p>
            ) : (
              <>
                <div className="adm-design-cards">
                <div className="adm-design-card">
                  <p className="adm-design-card-title">{t('admin.campaigns.designMenu')}</p>
                  <p className="adm-design-card-hint">{t('admin.campaigns.designMenuHint')}</p>
                  <div className="adm-design-menu">
                    <input
                      type="text" className="adm-design-icon" maxLength={8}
                      value={draft.diseno.menuIcon ?? ''}
                      onChange={(e) => patchDiseno('menuIcon', e.target.value)}
                      placeholder="🚀" aria-label={t('admin.campaigns.designIcon')}
                    />
                    <input
                      type="text"
                      value={draft.diseno.menuTitle ?? ''}
                      onChange={(e) => patchDiseno('menuTitle', e.target.value)}
                      placeholder={t('admin.campaigns.designMenuTitlePh')}
                      aria-label={t('admin.campaigns.designMenuTitle')}
                    />
                  </div>
                </div>

                <div className="adm-design-card">
                  <p className="adm-design-card-title">{t('admin.campaigns.designSections')}</p>
                  <p className="adm-design-card-hint">{t('admin.campaigns.designSectionsHint')}</p>
                  <div className="adm-toggles-row">
                    {([
                      ['showStreak', 'designShowStreak'],
                      ['showRecent', 'designShowRecent'],
                    ] as [string, string][]).map(([key, label]) => (
                      <label key={key} className="adm-toggle-row">
                        <AdminToggle isOn={disenoBool(key)} onChange={(v) => patchDiseno(key, v ? 'true' : '')} />
                        {t(`admin.campaigns.${label}`)} <ToggleHelp tooltipKey={label} />
                      </label>
                    ))}
                  </div>
                </div>

                <div className="adm-design-card">
                  {/* Banner "Brillamos Juntos" propio de esta campaña — mismo formulario
                      (`BannerEditorFields`, adminShared.tsx) que el paso Banner del wizard "Campañas
                      por OU" (CampaignExceptions.tsx): subtítulo + logo por idioma + preview. Vacío =
                      hereda el banner global/de programa. */}
                  <p className="adm-design-card-title">{t('admin.campaigns.bannerDesign')}</p>
                  <BannerEditorFields
                    value={{ subtitle: draft.bannerSubtitle, logoUrl: draft.bannerLogoUrl }}
                    onChange={(next) => patch({ bannerSubtitle: next.subtitle, bannerLogoUrl: next.logoUrl })}
                    scopeLabel={t('admin.campaigns.bannerScopeLabel')}
                  />
                </div>
                </div>
              </>
            )}
          </>
        )}

        {step === 'audience' && (
          <>
            {/* Bug UAT #22 (red de seguridad) — admin acotado sin OUs en su alcance de Brillamos:
                aviso explícito en vez de un árbol en blanco que bloquea el paso sin decir por qué. */}
            {scopeEmpty ? (
              <p className="adm-note adm-note--warn" role="alert">
                <i className="fas fa-triangle-exclamation" /> {t('admin.campaigns.scopeEmpty')}
              </p>
            ) : (<>
            {/* WP-01 (§26/RF-069/078) — árbol OU jerárquico con cascadeo padre→hijos (reusa el mismo
                OuTreeSelect que Documentación), en vez de chips planos hardcodeados. */}
            <div className="adm-field" role="group" aria-labelledby="camp-scope-label">
              <span id="camp-scope-label">{t('admin.campaigns.fieldScope')}</span>
              <OuTreeSelect items={scopedItems} mode="multi" variant="inline" value={draft.alcance} onChange={(alcance) => patch({ alcance })} />
            </div>
            {/* RF-071 — el conflicto se ve AQUÍ, en el paso donde se arregla, y no como un 409 tres
                pasos después. Bloquea el envío a aprobación vía `publishErrors`. */}
            {conflictos.length > 0 && (
              <p className="adm-note adm-note--warn" role="alert">
                <i className="fas fa-triangle-exclamation" />{' '}
                {t('admin.campaigns.scopeConflict', {
                  campanas: conflictos.map((c) => pickLocalized(c.nombre, i18n.language)).join(', '),
                })}
              </p>
            )}
            {activasIncompletas && (
              <p className="adm-note"><i className="fas fa-circle-info" /> {t('admin.campaigns.scopeConflictPartial')}</p>
            )}
            {/* QA (2026-09-01) — se quitó el segundo árbol de OUs de este paso (el de exclusión):
                confundía a los usuarios porque cualquier OU no marcada en Alcance ya queda excluida
                por defecto; marcarlas explícitamente era redundante. El campo correspondiente del
                borrador se deja intacto en el modelo de datos (siempre vacío en campañas nuevas) y se
                sigue enviando tal cual al guardar — no se tocó el contrato con el backend, ni la
                variable que decide si el árbol de Alcance se filtra al scope del admin (línea ~1727,
                que sigue defendiéndose de exclusiones heredadas de campañas guardadas ANTES de este
                cambio). El resumen de solo-lectura en Aprobaciones (`CampaignDetail`) tampoco se tocó
                a propósito: sigue mostrando las exclusiones de campañas viejas que ya las tuvieran. */}
            </>)}
          </>
        )}

        {step === 'config' && (
          <>
            <div className="adm-form-grid">
              <label className="adm-field"><span>{t('admin.campaigns.fieldStart')}</span><input type="date" min={minFechaCampana(hoy, original.fechaInicio)} value={draft.fechaInicio} onChange={(e) => patch({ fechaInicio: e.target.value })} /></label>
              <label className="adm-field"><span>{t('admin.campaigns.fieldEnd')}</span><input type="date" min={minFechaCampana(hoy, original.fechaFin)} value={draft.fechaFin} onChange={(e) => patch({ fechaFin: e.target.value })} /></label>
              {/* El selector de zona horaria viene de develop (#53); el mínimo de racha, de RF-037. */}
              <label className="adm-field"><span>{t('admin.campaigns.fieldTimezone')}</span>
                <select className="field" value={draft.zonaHoraria} onChange={(e) => patch({ zonaHoraria: e.target.value })}>
                  {TIMEZONES.map((tz) => <option key={tz} value={tz}>{tz}</option>)}
                </select>
              </label>
              {/* RF-037 — Mínimo de racha propio de la campaña. Vacío = hereda el global de Gestión. */}
              {/* Bug UAT #112 — tooltip de ayuda junto al rótulo: la nota inferior (streakMinimumHint)
                  queda lejos del campo, y en la sesión de Europa no quedó clara la función. */}
              <label className="adm-field"><span className="adm-field-head">{t('admin.campaigns.fieldStreakMinimum')}<ToggleHelp tooltipKey="streakMinimum" /></span>
                <input
                  type="number" min={STREAK_MINIMUM_FLOOR} max={STREAK_MINIMUM_CEILING} step={1}
                  className="adm-num-input"
                  disabled={locked}
                  value={draft.streakWeeklyMinimum}
                  placeholder={t('admin.campaigns.streakMinimumInherit')}
                  onChange={(e) => patch({ streakWeeklyMinimum: e.target.value })}
                  onBlur={(e) => {
                    const edit = parseStreakMinimum(e.target.value);
                    patch({ streakWeeklyMinimum: edit === 'clear' ? '' : String(edit) });
                  }}
                />
              </label>
              {/* Tope de envíos propio de la campaña. Vacío = hereda el global de Gestión. A diferencia
                  del mínimo de racha, editable aunque la campaña ya esté Activa: el tope se revisa por
                  envío individual, no al cierre de un período, así que no hay nada que recalificar. */}
              {/* Bug UAT #18 — tres estados del tope: número propio · vacío = hereda el global de
                  Gestión · «No aplica» = sin tope, ignora el global. El check desactiva el número. */}
              <div className="adm-field"><span className="adm-field-head">{t('admin.campaigns.fieldRecognitionsLimitOwn')}<ToggleHelp tooltipKey="recognitionsLimitOwn" /></span>
                <input
                  type="number" min={RECOGNITIONS_LIMIT_FLOOR} max={RECOGNITIONS_LIMIT_CEILING} step={1}
                  // BUG (Julio, 2026-08-21) — con solo el modificador (sin la clase base `adm-num-input`,
                  // que trae borde/padding/tipografía) este input rendía con los defaults del navegador:
                  // 5px más bajo que el de "Mínimo de racha" de al lado, que sí lleva ambas clases.
                  className="adm-num-input adm-num-input--md"
                  disabled={draft.recognitionsLimitDisabled}
                  value={draft.recognitionsLimitDisabled ? '' : draft.maxRecognitionsPerPeriod}
                  placeholder={t(draft.recognitionsLimitDisabled
                    ? 'admin.campaigns.recognitionsLimitNotApplicableShort'
                    : 'admin.campaigns.recognitionsLimitOwnInherit')}
                  onChange={(e) => patch({ maxRecognitionsPerPeriod: e.target.value })}
                  onBlur={(e) => {
                    const edit = parseMaxRecognitionsPerPeriod(e.target.value);
                    patch({ maxRecognitionsPerPeriod: edit === 'clear' ? '' : String(edit) });
                  }}
                />
                <label className="adm-toggle-row adm-field-subcheck">
                  <AdminToggle
                    isOn={draft.recognitionsLimitDisabled}
                    onChange={(next) => patch(next
                      // Al activar «No aplica» se limpia el número: son estados excluyentes.
                      ? { recognitionsLimitDisabled: true, maxRecognitionsPerPeriod: '' }
                      : { recognitionsLimitDisabled: false })}
                  />
                  <span>{t('admin.campaigns.recognitionsLimitNotApplicable')}</span>
                  <ToggleHelp tooltipKey="recognitionsLimitDisabled" />
                </label>
              </div>
              {/* Bug UAT #18 — «Veces mismo fundamental por persona»: número propio · vacío = hereda el
                  global de Gestión · «No aplica» = sin límite (en Europa el backend mantiene el piso GDPR). */}
              <div className="adm-field"><span className="adm-field-head">{t('admin.campaigns.fieldSameFundamentalLimit')}<ToggleHelp tooltipKey="sameFundamentalLimit" /></span>
                <input
                  type="number" min={RECOGNITIONS_LIMIT_FLOOR} max={RECOGNITIONS_LIMIT_CEILING} step={1}
                  // BUG (Julio, 2026-08-21) — mismo defecto que "Tope de envíos" arriba: faltaba la
                  // clase base `adm-num-input`.
                  className="adm-num-input adm-num-input--md"
                  disabled={draft.sameFundamentalLimitDisabled}
                  value={draft.sameFundamentalLimitDisabled ? '' : draft.maxSameFundamentalPerPerson}
                  placeholder={t(draft.sameFundamentalLimitDisabled
                    ? 'admin.campaigns.sameFundamentalNotApplicableShort'
                    : 'admin.campaigns.sameFundamentalInherit')}
                  onChange={(e) => patch({ maxSameFundamentalPerPerson: e.target.value })}
                  onBlur={(e) => {
                    const edit = parseMaxRecognitionsPerPeriod(e.target.value);
                    patch({ maxSameFundamentalPerPerson: edit === 'clear' ? '' : String(edit) });
                  }}
                />
                <label className="adm-toggle-row adm-field-subcheck">
                  <AdminToggle
                    isOn={draft.sameFundamentalLimitDisabled}
                    onChange={(next) => patch(next
                      ? { sameFundamentalLimitDisabled: true, maxSameFundamentalPerPerson: '' }
                      : { sameFundamentalLimitDisabled: false })}
                  />
                  <span>{t('admin.campaigns.sameFundamentalNotApplicable')}</span>
                  <ToggleHelp tooltipKey="sameFundamentalLimitDisabled" />
                </label>
              </div>
              {/*
                * BUG (Julio, 2026-08-21) — «Frecuencia» (C2/P17) se retira de la CAPTURA: sin uso real
                * en producción, generaba confusión en el wizard. Se quita solo el campo visual — el
                * dato (`draft.frecuencia`/`frecuenciaAGuardar`) sigue viajando en el PATCH tal como
                * llegó, mismo criterio ya aplicado aquí abajo para «Meta de participantes» (C2/P19):
                * una campaña con una frecuencia ya guardada por otra vía no la pierde al re-guardarse
                * por un motivo distinto.
                */}
              {/*
                * C2/P19 — «Meta de participantes» YA NO SE CAPTURA. Bug UAT #19 (Sesión 1 México,
                * 10/08/2026): «Campo Meta de campaña no aplica a Reconocimiento/Brillamos — viene de
                * Pulso y confunde». Acción acordada: ocultarlo.
                *
                * El #18 lo había ocultado SOLO para `tipo === 'Brillamos'`, y ahí estaba el resto del
                * bug: las tres pestañas de esta pantalla (Fundamentales / Brillamos / Personalizadas)
                * son TODAS de Reconocimiento, así que en «Personalizadas» el campo seguía a la vista.
                * El gate correcto no era el tipo: es la pantalla entera.
                *
                * Se retira sólo la CAPTURA, no el dato: `metaParticipantes` sigue en el contrato y el
                * PATCH lo devuelve tal como llegó del DTO (ver `save`), así que una meta ya guardada
                * no se borra al editar la campaña por otro motivo. La columna «% de meta» de
                * Históricos para la que se pensó nunca se llegó a construir (`HistoryTab` no la
                * pinta), por eso nadie perdía información al esconderlo.
                */}
            </div>
            <p className="adm-note"><i className="fas fa-circle-info" /> {t('admin.campaigns.streakMinimumHint')}</p>
            {/* El backend lo rechaza con la campaña iniciada: cambiarlo recalificaría el período en
                curso y rompería la racha de la audiencia completa. Igual que los puntos, se avisa y se
                deshabilita en vez de dejar que el guardado falle con un 422. */}
            {locked && <p className="adm-note adm-note--warn"><i className="fas fa-lock" /> {t('admin.campaigns.streakMinimumLocked')}</p>}
            <p className="adm-note"><i className="fas fa-circle-info" /> {t('admin.campaigns.recognitionsLimitOwnHint')}</p>
            <div className="adm-field">
              <span>{t('admin.campaigns.visibility')}</span>
              <div className="adm-toggles-row">
                {([
                  ['mostrarRemitente', 'visSender'], ['mostrarFundamental', 'visFundamental'],
                  ['mostrarTexto', 'visText'], ['mostrarHabito', 'visHabit'],
                  ['enviados', 'visSent'], ['recibidos', 'visReceived'],
                  ['cargaMasiva', 'visBulk'], ['reaccionesHabilitadas', 'visReactions'],
                  ['mostrarEtiquetaCampana', 'visCampaignTag'], ['permitirDesdeFundamentales', 'visFromFundamentals'],
                  ['envioDesactivado', 'visSendingDisabled'],
                  /*
                   * «Configurable por Site» se retiró por completo (bug UAT #20, Sesión 1 México, 10/08/2026):
                   * nunca fue una opción real de la campaña, era una nota de la maqueta. El campo ya no existe
                   * en el dominio, el DTO, ni el permiso que solía gobernar en el backend
                   * (`CampaignOuWinnerIndex`) — una campaña hija siempre gana sobre su raíz ahora, sin gate.
                   */
                ] as [keyof CampaignVisibilityDto, string][])
                  .map(([key, label]) => (
                  <label key={key} className="adm-toggle-row">
                    <AdminToggle isOn={Boolean(draft.visibilidad[key])} onChange={(v) => patchVis({ [key]: v } as Partial<CampaignVisibilityDto>)} />
                    {t(`admin.campaigns.${label}`)} <ToggleHelp tooltipKey={label} />
                  </label>
                ))}
              </div>
            </div>
            {/* Decisión PO 2026-09-23 — «Permitir editar después de enviar» y el Modo Anti-Spoiler viven
                en la campaña. Van en su propio bloque y no en «Visibilidad»: no deciden qué muestra la
                tarjeta, sino qué puede hacer quien envía y quién ve lo recibido mientras dure la campaña. */}
            <div className="adm-field">
              <span>{t('admin.campaigns.postSendTitle')}</span>
              <div className="adm-toggles-row">
                {([
                  ['allowPostEdit', 'modPostEdit'],
                  ['antiSpoiler', 'modAntiSpoiler'],
                ] as [keyof CampaignVisibilityDto, string][])
                  .map(([key, label]) => (
                  <label key={key} className="adm-toggle-row">
                    <AdminToggle isOn={Boolean(draft.visibilidad[key])} onChange={(v) => patchVis({ [key]: v } as Partial<CampaignVisibilityDto>)} />
                    {t(`admin.campaigns.${label}`)} <ToggleHelp tooltipKey={label} />
                  </label>
                ))}
              </div>
              {/* Los nombres se mantienen porque así los llama el resto de la app; estas líneas dicen qué
                  hacen, siempre visibles porque la duda aparece ANTES de encenderlos. */}
              <p className="adm-note"><i className="fas fa-circle-info" /> {t('admin.campaigns.modPostEditDescription')}</p>
              <p className="adm-note"><i className="fas fa-circle-info" /> {t('admin.campaigns.modAntiSpoilerDescription')}</p>
            </div>
            {/* Pedido PO 2026-09-29 — Multi-persona, Multi-fundamental y el tope de Fundamentales son de
                cada campaña Brillamos (salen de la pestaña Brillamos). Los tres de Fundamentales van
                juntos porque dan un solo resultado: el máximo por reconocimiento. Personalizadas no. */}
            {draft.tipo === 'Brillamos' && (
              <div className="adm-field">
                <span>{t('admin.campaigns.rulesTitle')}</span>
                <div className="adm-toggles-row">
                  <label className="adm-toggle-row">
                    <AdminToggle isOn={multiEnvio.multipersona} onChange={(v) => patchVis({ multipersona: v })} />
                    {t('admin.campaigns.ruleMultiPersona')} <ToggleHelp tooltipKey="ruleMultiPersona" />
                  </label>
                  <label className="adm-toggle-row">
                    <AdminToggle isOn={multiEnvio.multiFundamental} onChange={(v) => patchVis({ multiFundamental: v })} />
                    {t('admin.campaigns.ruleMultiFundamental')} <ToggleHelp tooltipKey="ruleMultiFundamental" />
                  </label>
                  {multiEnvio.multiFundamental && (
                    <label className="adm-toggle-row">
                      <AdminToggle isOn={multiEnvio.fundamentalsLimitActive} onChange={(v) => patchVis({ fundamentalsLimitActive: v })} />
                      {t('admin.campaigns.limitFundamentals')} <ToggleHelp tooltipKey="fundamentalsLimitActive" />
                    </label>
                  )}
                </div>
                {/* El número solo se muestra si aplica: antes la pestaña Brillamos enseñaba un 4 que, con
                    su interruptor apagado, no hacía nada. */}
                {multiEnvio.multiFundamental && multiEnvio.fundamentalsLimitActive && (
                  <label className="adm-field"><span>{t('admin.campaigns.limitFundamentals')}</span>
                    <input type="number" min={1} max={50} step={1} value={multiEnvio.maxFundamentalsPerRecognition}
                      onChange={(e) => patchVis({ maxFundamentalsPerRecognition: Math.max(1, Math.min(50, Math.round(Number(e.target.value) || 1))) })} />
                  </label>
                )}
                {multiEnvio.multiFundamental && !multiEnvio.fundamentalsLimitActive && (
                  <p className="adm-note"><i className="fas fa-circle-info" /> {t('admin.campaigns.multiEnvioNoLimitHint')}</p>
                )}
                {avisoEuropa && (
                  <p className="adm-note adm-note--warn"><i className="fas fa-triangle-exclamation" /> {t('admin.campaigns.multiEnvioEuropaAviso')}</p>
                )}
              </div>
            )}
            {/* Pedido 2026-09-29 — la modalidad de reconocimiento (entry points) es de cada campaña
                Brillamos (sale de la pestaña Brillamos). Single-select: los 3 switches como radio. */}
            {draft.tipo === 'Brillamos' && (
              <div className="adm-field">
                <span>{t('admin.campaigns.entryPoints')} <ToggleHelp tooltipKey="entryPoints" /></span>
                <div className="adm-toggles-row">
                  {(['A', 'B', 'both'] as const).map((m) => (
                    <label key={m} className="adm-inline-toggle adm-inline-toggle--switch">
                      <input type="checkbox" checked={modalidad === m} onChange={() => patchVis({ modalidadEntrada: m })} /> {t(`admin.campaigns.entry.${m}`)}
                    </label>
                  ))}
                </div>
              </div>
            )}
            <div className="adm-field">
              <span>{t('admin.campaigns.anonymousOverrideLabel')}</span>
              <select className="field"
                value={draft.visibilidad.permitirAnonimos === null || draft.visibilidad.permitirAnonimos === undefined
                  ? '' : String(draft.visibilidad.permitirAnonimos)}
                onChange={(e) => patchVis({
                  permitirAnonimos: e.target.value === '' ? null : e.target.value === 'true',
                })}
              >
                <option value="">{t('admin.campaigns.anonymousOverrideInherit')}</option>
                <option value="true">{t('admin.campaigns.anonymousOverrideAllow')}</option>
                <option value="false">{t('admin.campaigns.anonymousOverrideDeny')}</option>
              </select>
              <small className="adm-note">{t('admin.campaigns.anonymousOverrideHint')}</small>
            </div>
            <div className="adm-field">
              <span>{t('admin.campaigns.points')}</span>
              {locked && <p className="adm-note adm-note--warn"><i className="fas fa-lock" /> {t('admin.campaigns.pointsLocked')}</p>}
              <label className="adm-toggle-row adm-toggle-row--warn" title={t('admin.campaigns.pointsHint')}>
                <AdminToggle isOn={draft.visibilidad.aplicanPuntos} disabled={locked} onChange={(v) => patchVis({ aplicanPuntos: v })} />
                {t('admin.campaigns.applyPoints')} <ToggleHelp tooltipKey="applyPoints" /> <i className="fas fa-triangle-exclamation" />
              </label>
            </div>
          </>
        )}

        {step === 'documents' && (
          draft.id
            ? <CampaignDocumentsStep campaignId={draft.id} />
            : <p className="adm-note"><i className="fas fa-circle-info" /> {t('admin.campaigns.saveDraftFirst')}</p>
        )}

        {step === 'publish' && (
          <>
            <dl className="adm-kv">
              <dt>{t('admin.campaigns.colType')}</dt><dd>{t(`admin.campaigns.type.${draft.tipo}`)}</dd>
              <dt>{t('admin.campaigns.colStatus')}</dt><dd><span className={statusBadge(draft.estado)}>{t(`admin.campaigns.status.${draft.estado}`)}</span></dd>
              <dt>{t('admin.campaigns.fieldScope')}</dt><dd>{draft.alcance.length ? ouLabels(collapseOuIds(draft.alcance, ouItems), ouItems) : '—'}</dd>
              <dt>{t('admin.campaigns.dates')}</dt><dd>{draft.fechaInicio || '—'} → {draft.fechaFin || '—'}</dd>
              <dt>{t('admin.campaigns.fieldStreakMinimum')}</dt>
              <dd>{streakMinimum ?? t('admin.campaigns.streakMinimumInherit')}</dd>
            </dl>
            {!canSave && <p className="adm-note adm-note--warn"><i className="fas fa-triangle-exclamation" /> {t('admin.campaigns.publishRequirements')}</p>}
            {approvalM.isError && <p className="adm-error" role="alert"><i className="fas fa-triangle-exclamation" /> {getRecognitionErrorMessage(approvalM.error, t('admin.saveError'))}</p>}
            {/* BUG (Julio, 2026-08-21) — el aviso de envío exitoso pasaba casi desapercibido con el
                estilo neutro de `.adm-note`. Reusa los modificadores ya existentes (verde, negrita)
                en vez de duplicarlos; `--lg` es el único nuevo, solo para el tamaño. */}
            {approvalM.isSuccess && <p className="adm-note adm-note--ok adm-note--strong adm-note--lg"><i className="fas fa-check" /> {t('admin.campaigns.approvalSent')}</p>}
          </>
        )}
      </div>

      {/* WP-03 — errores del paso actual (solo tras intentar avanzar) */}
      {touched[step] && currentErrors.length > 0 && (
        <ul className="adm-wizard-errors" role="alert">
          {currentErrors.map((e) => <li key={e}><i className="fas fa-triangle-exclamation" /> {e}</li>)}
        </ul>
      )}
      {saveError ? <p className="adm-error" role="alert"><i className="fas fa-triangle-exclamation" /> {campaignErrorMessage(saveError, t)}</p> : null}
      {/* El motivo del botón apagado solo se explica cuando el botón está a la vista (bug UAT #16):
          en los pasos de captura de un alta nueva no hay Guardar que justificar, y ahí este aviso se
          leía como un error del paso actual. Su lugar lo toma `saveDraftLater`, abajo. */}
      {showSave && !canSave && step !== 'publish' && <p className="adm-note adm-note--warn"><i className="fas fa-triangle-exclamation" /> {t('admin.campaigns.publishRequirements')}</p>}

      <div className="adm-wizard-nav">
        <button type="button" className="btn btn--secondary" onClick={onDone}>
          {t('common.cancel')}
        </button>
        <button type="button" className="btn btn--secondary" disabled={stepIndex === 0} onClick={() => setStep(STEPS[Math.max(0, stepIndex - 1)])}>
          <i className="fas fa-chevron-left" /> {t('admin.campaigns.prev')}
        </button>
        {showSave
          ? <AdminSaveBar onSave={() => save()} saving={saving} dirty={dirty && canSave} saved={createM.isSuccess || updateM.isSuccess}
              // «Guardar borrador» solo mientras LO ES: en una campaña Activa/Finalizada el guardado
              // no crea ningún borrador y el rótulo mentiría.
              label={draft.estado === 'Borrador' ? t('admin.campaigns.saveDraft') : undefined} />
          : <p className="adm-wizard-savehint"><i className="fas fa-circle-info" /> {t('admin.campaigns.saveDraftLater')}</p>}
        {isLast ? (
          // BUG (Julio, 2026-08-21) — el botón seguía activo después de un envío exitoso: nada impedía
          // mandar la misma campaña a aprobación dos veces. `approvalM.isSuccess` se resetea solo al
          // desmontar el wizard (nueva instancia de `useCampaignApproval`), así que apagarlo aquí no
          // bloquea un reenvío legítimo en una sesión nueva del wizard (p. ej. tras un rechazo).
          <button type="button" className="btn btn--primary"
            disabled={!draft.id || approvalM.isPending || approvalM.isSuccess || publishErrors.length > 0}
            onClick={() => setConfirmSubmit(true)}>
            <i className="fas fa-paper-plane" /> {t('admin.campaigns.submitApproval')}
          </button>
        ) : (
          <button type="button" className="btn btn--primary" onClick={() => goTo(STEPS[Math.min(STEPS.length - 1, stepIndex + 1)])}>
            {t('admin.campaigns.next')} <i className="fas fa-chevron-right" />
          </button>
        )}
      </div>
      </div>
      </div>
      {confirmSubmit && (
        <ConfirmModal
          type="info"
          title={t('admin.campaigns.submitApprovalConfirmTitle')}
          message={t('admin.campaigns.submitApprovalConfirmMsg')}
          confirmText={t('admin.campaigns.submitApproval')}
          onConfirm={() => save(() => draft.id && approvalM.mutate({ id: draft.id, action: 'submit' }))}
          onClose={() => setConfirmSubmit(false)}
        />
      )}
    </AdminSection>
  );
}

// ─── Paso Documentos ──────────────────────────────────────────────────────────
// WP-08 (§26) — el formulario de alta + tabla de documentos vive en `DocumentsManager`
// (`adminShared.tsx`, Tarea 15 SDD 2026-08-17): lo comparte con el paso "Documentos" del wizard de
// excepciones (`CampaignExceptions.tsx`), que apunta a los endpoints de excepción en vez de campaña.
// Este componente solo cablea los hooks reales de CAMPAÑA sobre ese formulario controlado.
//
// Tarea 16 (SDD 2026-08-17) — nota explicativa del nivel 4 de `CampaignDocs.tsx` (visor público): si
// esta campaña no aporta documentos propios, el visor cae a los de la sección "Documentos" de la
// administración (corporativos), NO a un estado vacío.
function CampaignDocumentsStep({ campaignId }: Readonly<{ campaignId: string }>) {
  const { t } = useTranslation('recognition');
  const docs = useCampaignDocuments(campaignId);
  const addM = useAddCampaignDocument();
  const delM = useDeleteCampaignDocument();

  return (
    <>
      <p className="adm-note"><i className="fas fa-circle-info" /> {t('admin.campaigns.docsFallbackNote')}</p>
      <DocumentsManager
        docs={docs.data ?? []}
        isLoading={docs.isLoading}
        isError={docs.isError}
        error={docs.error}
        onRetry={() => { docs.refetch(); }}
        onAdd={(input) => addM.mutate({ id: campaignId, input })}
        addPending={addM.isPending}
        addError={addM.isError ? addM.error : null}
        addSuccess={addM.isSuccess}
        onAddReset={() => { if (addM.isSuccess || addM.isError) addM.reset(); }}
        onDelete={(documentId) => delM.mutate(documentId)}
        deletePending={delM.isPending}
      />
    </>
  );
}

// ─── Aprobaciones pendientes (AdminGlobal / Admin Reconocimiento) ─────────────
// WP-04 — resuelve el nombre del solicitante (degrada al id si el directorio no responde).
function RequesterName({ userId }: Readonly<{ userId: string }>) {
  const { data } = useUserById(userId);
  return <>{data?.displayName?.trim() || userId}</>;
}

// WP-04b — resuelve el nombre de la campaña en el idioma actual (degrada al id si no carga).
function CampaignNameCell({ campaignId }: Readonly<{ campaignId: string }>) {
  const { i18n } = useTranslation('recognition');
  const { data } = useCampaign(campaignId);
  const lang = i18n.language.split('-')[0];
  return <>{(data && getCampaignName(data, lang)) || campaignId}</>;
}

// WP-04b — detalle de solo lectura de la campaña a aprobar, para que el AdminGlobal vea QUÉ aprueba
// (tipo, descripción, fechas, alcance/exclusiones) sin salir de la tabla de aprobaciones.
function CampaignDetail({ campaignId }: Readonly<{ campaignId: string }>) {
  const { t, i18n } = useTranslation('recognition');
  const { data, isLoading, isError } = useCampaign(campaignId);
  // rootsOnly: false — mismo motivo que la tabla principal: resolver nombres de OUs profundas en Alcance/Exclusiones.
  // activeOnly: false — mismo motivo que la tabla principal: ramas país inactivas no deben romper la cadena de padres.
  const { data: ouItems = [] } = useOUItems({ activeOnly: false, rootsOnly: false });
  const lang = i18n.language.split('-')[0];
  if (isLoading) return <p className="adm-note">{t('common.loading')}</p>;
  if (isError || !data) return <p className="adm-note adm-note--warn"><i className="fas fa-triangle-exclamation" /> {t('admin.saveError')}</p>;
  return (
    <dl className="adm-kv">
      <dt>{t('admin.campaigns.colType')}</dt><dd>{t(`admin.campaigns.type.${data.tipo}`)}</dd>
      <dt>{t('admin.campaigns.fieldDescription')}</dt><dd>{data.descripcion?.[lang]?.trim() || '—'}</dd>
      <dt>{t('admin.campaigns.dates')}</dt><dd>{(data.fechaInicio?.slice(0, 10)) || '—'} → {(data.fechaFin?.slice(0, 10)) || '—'}</dd>
      <dt>{t('admin.campaigns.fieldScope')}</dt><dd>{data.alcance?.length ? ouLabels(collapseOuIds(data.alcance, ouItems), ouItems) : t('admin.campaigns.allScopes')}</dd>
      <dt>{t('admin.campaigns.fieldExclusions')}</dt><dd>{data.exclusiones?.length ? ouLabels(collapseOuIds(data.exclusiones, ouItems), ouItems) : '—'}</dd>
    </dl>
  );
}

function ApprovalsPanel({ onBack, focusCampaignId }: Readonly<{ onBack: () => void; focusCampaignId?: string }>) {
  const { t, i18n } = useTranslation('recognition');
  const pending = usePendingApprovals();
  const approvalM = useCampaignApproval();
  const list = pending.data ?? [];
  const fmt = (iso: string) => new Date(iso).toLocaleString(i18n.language);

  // WP-04 — el rechazo exige comentario (el backend lo valida). Se captura inline por fila; el botón
  // "Rechazar" abre el cuadro y "Confirmar rechazo" envía el comentario. Aprobar es directo.
  const [rejecting, setRejecting] = useState<{ campaignId: string; comment: string } | null>(null);
  const [confirmApprove, setConfirmApprove] = useState<string | null>(null);
  // WP-04b — "ver detalle": una fila expandida a la vez con el resumen de solo lectura de la campaña.
  const [detailId, setDetailId] = useState<string | null>(null);

  // Deep-link ?aprobacion={id}: al llegar la cola, expandir esa campaña si SIGUE pendiente (otro admin
  // pudo resolverla ya; en ese caso no está en la lista y no se expande nada — validación implícita).
  // Solo una vez: si el admin cierra el detalle, no se vuelve a forzar.
  const [focusApplied, setFocusApplied] = useState(false);
  useEffect(() => {
    if (focusApplied || !focusCampaignId || pending.isLoading) return;
    if (list.some((a) => a.campaignId === focusCampaignId)) setDetailId(focusCampaignId);
    setFocusApplied(true);
  }, [focusApplied, focusCampaignId, pending.isLoading, list]);

  // Ticket 04 — si el enlace apunta a una campaña que ya no está en la cola, se pregunta por ella: un
  // 404 significa que se BORRÓ (o nunca existió) y se avisa en vez de abrir la cola sin explicación.
  // Si existe, otro admin ya la resolvió y basta con no expandir nada, como siempre.
  const focoFueraDeCola = Boolean(focusCampaignId) && !pending.isLoading
    && !list.some((a) => a.campaignId === focusCampaignId);
  const foco = useCampaign(focoFueraDeCola ? focusCampaignId : undefined);
  const focoInexistente = focoFueraDeCola
    && foco.error instanceof RecognitionApiError && foco.error.status === 404;

  return (
    <AdminSection title={t('admin.campaigns.approvalsTitle')} description={t('admin.campaigns.approvalsSub')}>
      <div className="adm-toolbar">
        <button type="button" className="btn btn--link" onClick={onBack}><i className="fas fa-arrow-left" /> {t('admin.campaigns.backToList')}</button>
      </div>
      {focoInexistente && (
        <p className="adm-warn" role="status"><i className="fas fa-circle-info" /> {t('admin.campaigns.approvalCampaignGone')}</p>
      )}
      <StateBlock isLoading={pending.isLoading} isError={pending.isError} error={pending.error} isEmpty={list.length === 0} emptyText={t('admin.campaigns.approvalsEmpty')} onRetry={() => { pending.refetch(); }}>
        <div className="adm-table-wrap">
          <table className="adm-table">
            <thead><tr><th>{t('admin.campaigns.colCampaign')}</th><th>{t('admin.campaigns.requestedBy')}</th><th>{t('admin.campaigns.colDate')}</th><th /></tr></thead>
            <tbody>
              {list.map((a) => (
                <Fragment key={a.id}>
                  <tr>
                    <td><CampaignNameCell campaignId={a.campaignId} /></td>
                    <td><RequesterName userId={a.solicitadoPor} /></td>
                    <td>{fmt(a.solicitadoAt)}</td>
                    <td>
                      <div className="adm-actions">
                        <button type="button" className="btn btn--secondary" aria-expanded={detailId === a.campaignId} onClick={() => setDetailId((cur) => (cur === a.campaignId ? null : a.campaignId))}>
                          <i className="fas fa-eye" /> {t('admin.campaigns.viewDetail')}
                        </button>
                        <button type="button" className="btn btn--primary" disabled={approvalM.isPending} onClick={() => setConfirmApprove(a.campaignId)}><i className="fas fa-check" /> {t('admin.campaigns.approve')}</button>
                        <button type="button" className="btn btn--danger" disabled={approvalM.isPending} onClick={() => setRejecting({ campaignId: a.campaignId, comment: '' })}><i className="fas fa-xmark" /> {t('admin.campaigns.reject')}</button>
                      </div>
                    </td>
                  </tr>
                  {detailId === a.campaignId && (
                    <tr>
                      <td colSpan={4}><CampaignDetail campaignId={a.campaignId} /></td>
                    </tr>
                  )}
                  {rejecting?.campaignId === a.campaignId && (
                    <tr>
                      <td colSpan={4}>
                        <div className="adm-reject-box">
                          <label className="adm-field">
                            <span>{t('admin.campaigns.rejectReason')}</span>
                            <textarea rows={2} value={rejecting.comment} autoFocus
                              onChange={(e) => setRejecting({ campaignId: a.campaignId, comment: e.target.value })} />
                          </label>
                          <div className="adm-actions">
                            <button type="button" className="btn btn--danger" disabled={!rejecting.comment.trim() || approvalM.isPending}
                              onClick={() => approvalM.mutate(
                                { id: a.campaignId, action: 'reject', comentario: rejecting.comment.trim() },
                                { onSuccess: () => setRejecting(null) })}>
                              <i className="fas fa-xmark" /> {t('admin.campaigns.rejectConfirm')}
                            </button>
                            <button type="button" className="btn btn--secondary" onClick={() => setRejecting(null)}>{t('common.cancel')}</button>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
        {approvalM.isError && <p className="adm-error" role="alert"><i className="fas fa-triangle-exclamation" /> {getRecognitionErrorMessage(approvalM.error, t('admin.saveError'))}</p>}
      </StateBlock>
      {confirmApprove && (
        <ConfirmModal
          type="info"
          title={t('admin.campaigns.approveConfirmTitle')}
          message={t('admin.campaigns.approveConfirmMsg')}
          confirmText={t('admin.campaigns.approve')}
          onConfirm={() => approvalM.mutate({ id: confirmApprove, action: 'approve' })}
          onClose={() => setConfirmApprove(null)}
        />
      )}
    </AdminSection>
  );
}

export default AdminCampaigns;
